import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useRegisterBreadcrumbTitle } from "@/context/BreadcrumbContext";
import { toast } from "sonner";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  useWorkflowQuery,
  useCreateWorkflowMutation,
  useUpdateWorkflowMutation,
  useSaveFiltersMutation,
  useSaveLeadsMutation,
  useActivateWorkflowMutation,
  getWorkflowSendingStatus,
  type PreviewLead,
} from "@/hooks/useWorkflows";
import {
  useSequencesQuery,
  useSequenceQuery,
  useCreateSequenceMutation as useCreateSeqMutation,
  useUpdateSequenceMutation as useUpdateSeqMutation,
  useActivateSequenceMutation as useActivateSeqMutation,
  useAddStepMutation,
  useUpdateStepMutation,
  useDeleteStepMutation,
  useAddVariantMutation,
  useUpdateVariantMutation,
  useDeleteVariantMutation,
} from "@/hooks/useSequences";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/apiClient";
import { API_URL, STORAGE } from "@/_config";
import { extractEmailBody } from "@/lib/utils";
import {
  useCampaignsQuery,
  useCampaignQuery,
  useCampaignGoalsQuery,
  useCreateCampaignMutation,
  type CampaignGoal,
} from "@/hooks/useCampaigns";
import {
  useSenderIdentitiesQuery,
  useCreateSenderIdentityMutation,
  type SenderIdentity,
} from "@/hooks/useSenderIdentity";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import EmailEditor from "@/components/sequences/EmailEditor";
import { format, parse, isValid } from "date-fns";
import {
  ArrowLeft,
  ArrowRight,
  Users,
  FileText,
  GitBranch,
  Rocket,
  CheckCircle,
  CheckCircle2,
  Plus,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Calendar,
  Sparkles,
  Star,
  Send,
  Eye,
  MessageSquare,
  Search,
  Wand2,
  Tag,
  Building2,
  User,
  Briefcase,
  Globe,
  BarChart3,
  Reply,
  Loader2,
  ShieldCheck,
  GitFork,
  MailOpen,
  MousePointerClick,
  Ban,
  PhoneCall,
  UserCheck,
  Pause,
  SkipForward,
  Trash2,
  Shield,
  Link2,
  ToggleRight,
  ToggleLeft,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface FilterItem {
  field: string;
  operator: string;
  value: string;
}

interface VariableItem {
  tag: string;
  label: string;
}

interface VariableCategory {
  label: string;
  icon: string;
  vars: VariableItem[];
}

interface AiTemplate {
  id: string;
  label: string;
  desc: string;
  icon: string;
}

interface EngagementTrigger {
  id: string;
  label: string;
  icon: LucideIcon;
  desc: string;
}

interface EngagementAction {
  id: string;
  label: string;
  icon: LucideIcon;
  color: string;
}

interface StepTemplate {
  id: string;
  label: string;
  desc: string;
  icon: LucideIcon;
  color: string;
  subject: string;
  body: string;
  delay: number;
}

interface SmartCondition {
  id: string;
  label: string;
  trigger: string;
  action: string;
  icon: LucideIcon;
  color: string;
  enabled: boolean;
}

interface SequenceStep {
  id: number;
  type: string;
  subject: string;
  body: string;
  delay: number;
  color: string;
  label: string;
}

interface TimelineDayEntry {
  stepNum: number;
  label: string;
  emailCount: number;
}

interface TimelineDay {
  day: number;
  entries: TimelineDayEntry[];
}

const WF_TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "UTC",
  "Europe/London",
  "Europe/Paris",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Australia/Sydney",
];

function WfWindowTimePicker({
  value,
  onChange,
  onClose,
}: {
  value: string;
  onChange: (v: string) => void;
  onClose?: () => void;
}) {
  const [h24, m24str] = value.split(":");
  const curH = parseInt(h24);
  const curM = parseInt(m24str);
  const curH12 = curH % 12 || 12;
  const curPeriod = curH >= 12 ? "PM" : "AM";
  const setHour = (h12: number) => {
    let h = h12 % 12;
    if (curPeriod === "PM") h += 12;
    onChange(`${String(h).padStart(2, "0")}:${String(curM).padStart(2, "0")}`);
    // stay open — user still needs minute and AM/PM
  };
  const setMinute = (m: number) => {
    onChange(`${String(curH).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    // stay open — user still needs AM/PM
  };
  const setPeriod = (p: string) => {
    let h = curH;
    if (p === "PM" && curPeriod === "AM") h += 12;
    if (p === "AM" && curPeriod === "PM") h -= 12;
    h = Math.max(0, Math.min(23, h));
    onChange(`${String(h).padStart(2, "0")}:${String(curM).padStart(2, "0")}`);
    onClose?.(); // AM/PM is the final step — close
  };
  return (
    <div className="cbp-picker-popover cbp-time-popover">
      <div className="cbp-time-cols">
        <div className="cbp-time-col">
          <div className="cbp-time-col-label">Hour</div>
          <div className="cbp-time-scroll">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
              <button
                key={h}
                className={`cbp-time-item${curH12 === h ? " cbp-time-item--selected" : ""}`}
                onClick={() => setHour(h)}
              >
                {String(h).padStart(2, "0")}
              </button>
            ))}
          </div>
        </div>
        <div className="cbp-time-col">
          <div className="cbp-time-col-label">Min</div>
          <div className="cbp-time-scroll">
            {Array.from({ length: 60 }, (_, i) => i).map((m) => (
              <button
                key={m}
                className={`cbp-time-item${curM === m ? " cbp-time-item--selected" : ""}`}
                onClick={() => setMinute(m)}
              >
                {String(m).padStart(2, "0")}
              </button>
            ))}
          </div>
        </div>
        <div className="cbp-time-col">
          <div className="cbp-time-col-label">AM/PM</div>
          <div className="cbp-time-scroll">
            {["AM", "PM"].map((p) => (
              <button
                key={p}
                className={`cbp-time-item${curPeriod === p ? " cbp-time-item--selected" : ""}`}
                onClick={() => setPeriod(p)}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const bodyHasContent = (html: string): boolean => html.replace(/<[^>]*>/g, "").trim().length > 0;

const stepLabels: string[] = ["Select Leads", "Campaign", "Sequence", "Schedule", "Review & Launch"];
const stepIcons: LucideIcon[] = [Users, FileText, GitBranch, Clock, Rocket];
const segments: string[] = [
  "VP+ in Tech Companies",
  "C-Level at SaaS 200+",
  "All Active Leads",
  "Enterprise 1000+ Employees",
  "Engaged Leads Q4",
  "Marketing Directors",
];
const filterFields: string[] = ["Industry", "Role", "Company Size", "Location", "Source"];
const filterOperators: string[] = ["is", "is not", "contains", "starts with"];
const dayLabels: string[] = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const variableCategories: VariableCategory[] = [
  {
    label: "Contact",
    icon: "User",
    vars: [
      { tag: "{{first_name}}", label: "First Name" },
      { tag: "{{last_name}}", label: "Last Name" },
      { tag: "{{full_name}}", label: "Full Name" },
      { tag: "{{title}}", label: "Job Title" },
    ],
  },
  {
    label: "Company",
    icon: "Building2",
    vars: [
      { tag: "{{company}}", label: "Company Name" },
      { tag: "{{industry}}", label: "Industry" },
      { tag: "{{company_size}}", label: "Company Size" },
      { tag: "{{company_domain}}", label: "Company Domain" },
      { tag: "{{company_location}}", label: "HQ Location" },
    ],
  },
  {
    label: "Engagement",
    icon: "BarChart3",
    vars: [
      { tag: "{{last_response}}", label: "Last Response" },
      { tag: "{{last_opened_date}}", label: "Last Opened Date" },
      { tag: "{{response_sentiment}}", label: "Response Sentiment" },
      { tag: "{{engagement_score}}", label: "Engagement Score" },
    ],
  },
  {
    label: "AI-Powered",
    icon: "Sparkles",
    vars: [
      { tag: "{{ai_icebreaker}}", label: "AI Icebreaker" },
      { tag: "{{ai_pain_point}}", label: "AI Pain Point" },
      { tag: "{{ai_value_prop}}", label: "AI Value Proposition" },
      { tag: "{{ai_case_study}}", label: "AI Relevant Case Study" },
      { tag: "{{ai_personalized_cta}}", label: "AI Personalized CTA" },
      { tag: "{{ai_follow_up}}", label: "AI Follow-Up Based on Reply" },
    ],
  },
];

const catIcons: Record<string, LucideIcon> = { User, Building2, BarChart3, Sparkles };

const aiTemplates: AiTemplate[] = [
  {
    id: "icebreaker",
    label: "Personalized Icebreaker",
    desc: "AI-crafted opening based on recent company news, role, and industry",
    icon: "star",
  },
  {
    id: "pain_point",
    label: "Pain Point Analysis",
    desc: "Identify and address pain points specific to their business type and role",
    icon: "target",
  },
  {
    id: "value_prop",
    label: "Custom Value Proposition",
    desc: "Tailored value pitch based on their industry, company size, and challenges",
    icon: "sparkles",
  },
  {
    id: "case_study",
    label: "Relevant Case Study",
    desc: "Match and insert a case study from a similar company in their industry",
    icon: "file",
  },
  {
    id: "reply_aware",
    label: "Response-Aware Follow-up",
    desc: "AI adapts content based on whether they opened, clicked, or replied",
    icon: "reply",
  },
  {
    id: "competitor",
    label: "Competitive Intelligence",
    desc: "Reference how their competitors are solving similar problems",
    icon: "chart",
  },
];

const engagementTriggers: EngagementTrigger[] = [
  { id: "replied", label: "Replied", icon: Reply, desc: "Lead sent a response" },
  { id: "opened", label: "Opened", icon: MailOpen, desc: "Lead opened the email" },
  { id: "clicked", label: "Clicked Link", icon: MousePointerClick, desc: "Lead clicked a link in the email" },
  { id: "no_open", label: "No Open", icon: Ban, desc: "Lead did not open the email" },
  { id: "no_reply", label: "No Reply", icon: Ban, desc: "Lead opened but did not reply" },
  { id: "booked", label: "Booked Meeting", icon: Calendar, desc: "Lead booked a meeting" },
];

const engagementActions: EngagementAction[] = [
  { id: "continue", label: "Continue to next step", icon: ArrowRight, color: "var(--sky)" },
  { id: "skip", label: "Skip next step", icon: SkipForward, color: "var(--amber)" },
  { id: "pause", label: "Pause sequence", icon: Pause, color: "#FF9800" },
  { id: "stop", label: "Stop & mark as converted", icon: CheckCircle, color: "var(--green)" },
  { id: "move_stage", label: "Move to engaged stage", icon: UserCheck, color: "#7C4DFF" },
  { id: "notify", label: "Notify sales rep", icon: PhoneCall, color: "#E91E63" },
];

const stepTemplates: StepTemplate[] = [
  {
    id: "intro",
    label: "Introduction",
    desc: "First touchpoint — introduce yourself and your value",
    icon: Send,
    color: "#4AADE8",
    subject: "Quick question about {{company}}",
    body: "Hi {{first_name}},\n\n{{ai_icebreaker}}\n\nI work with {{title}}s in the {{industry}} space, and I noticed {{company}} might benefit from what we do.\n\n{{ai_value_prop}}\n\nWould you be open to a quick 15-minute chat this week?\n\nBest,\n[Your Name]",
    delay: 0,
  },
  {
    id: "follow_up",
    label: "Follow-Up",
    desc: "Gentle nudge if they haven't responded yet",
    icon: Reply,
    color: "#F5A623",
    subject: "Re: Quick question about {{company}}",
    body: "Hi {{first_name}},\n\nJust wanted to make sure my last note didn't get buried. I know {{title}}s at growing companies are always juggling priorities.\n\n{{ai_pain_point}}\n\nHappy to work around your schedule if this week doesn't work.\n\nBest,\n[Your Name]",
    delay: 3,
  },
  {
    id: "value",
    label: "Value Add",
    desc: "Share useful content — case study, insight, or resource",
    icon: Star,
    color: "#7C4DFF",
    subject: "Thought you'd find this useful, {{first_name}}",
    body: "Hi {{first_name}},\n\nI came across something I thought would be relevant for {{company}}:\n\n{{ai_case_study}}\n\nThe results speak for themselves. Would love to walk you through how this could look for your team.\n\nBest,\n[Your Name]",
    delay: 4,
  },
  {
    id: "breakup",
    label: "Break-Up",
    desc: "Final friendly message — creates urgency to respond",
    icon: MessageSquare,
    color: "#E91E63",
    subject: "Should I close your file, {{first_name}}?",
    body: "Hi {{first_name}},\n\nI've reached out a few times and haven't heard back, so I'll assume the timing isn't right.\n\nIf things change down the road, I'd love to reconnect. {{ai_personalized_cta}}\n\nWishing {{company}} all the best!\n\n[Your Name]",
    delay: 5,
  },
  {
    id: "custom",
    label: "Custom Step",
    desc: "Start with a blank step and write your own content",
    icon: FileText,
    color: "#607D8B",
    subject: "",
    body: "",
    delay: 3,
  },
];

const smartConditionPresets: SmartCondition[] = [
  {
    id: "stop_reply",
    label: "Stop if they reply",
    trigger: "replied",
    action: "stop",
    icon: Reply,
    color: "var(--green)",
    enabled: true,
  },
  {
    id: "notify_click",
    label: "Alert me if they click a link",
    trigger: "clicked",
    action: "notify",
    icon: MousePointerClick,
    color: "#E91E63",
    enabled: false,
  },
  {
    id: "stop_meeting",
    label: "Stop if they book a meeting",
    trigger: "booked",
    action: "stop",
    icon: Calendar,
    color: "var(--green)",
    enabled: true,
  },
  {
    id: "pause_open",
    label: "Pause if they open (let me follow up manually)",
    trigger: "opened",
    action: "pause",
    icon: MailOpen,
    color: "#FF9800",
    enabled: false,
  },
];

const sampleAiOutputs: Record<string, string> = {
  icebreaker: `Hi {{first_name}},\n\nI noticed {{company}} recently {{ai_icebreaker}} — congratulations on that milestone! Given your role as {{title}}, I imagine you're focused on scaling that momentum.`,
  pain_point: `As a {{title}} in the {{industry}} space, you're likely facing challenges around {{ai_pain_point}}. Companies of {{company}}'s size ({{company_size}} employees) often tell us this is their #1 bottleneck.`,
  value_prop: `{{ai_value_prop}}\n\nFor {{industry}} companies like {{company}}, we've seen teams reduce outbound effort by 60% while increasing qualified pipeline by 3x.`,
  case_study: `{{ai_case_study}}\n\nA similar {{industry}} company with {{company_size}} employees saw a 47% increase in response rates within the first 30 days.`,
  reply_aware: `{{ai_follow_up}}\n\n[If opened but no reply]: I wanted to make sure my last note didn't get buried — I know {{title}}s at {{company_size}}-person companies are always juggling priorities.\n\n[If replied positively]: Thanks for your interest! Based on what you shared, here's how we can help {{company}} specifically...\n\n[If no engagement]: Taking a different angle — {{ai_pain_point}}`,
  competitor: `I've been researching the {{industry}} landscape and noticed that several companies similar to {{company}} are investing heavily in AI-powered outbound. {{ai_value_prop}}`,
};

const timeOptions: string[] = [];
for (let h = 0; h < 24; h++) {
  const suffix = h < 12 ? "AM" : "PM";
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  timeOptions.push(`${hour}:00 ${suffix}`);
  timeOptions.push(`${hour}:30 ${suffix}`);
}

// Converts "HH:MM" (24h) to display "HH:MM AM/PM"
function fmt24hDisplay(time24: string): string {
  const [hStr, mStr] = time24.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  const suffix = h < 12 ? "AM" : "PM";
  const h12 = h % 12 || 12;
  return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${suffix}`;
}

// Converts "H:MM AM/PM" (old stored 12h format) to "HH:MM" (24h) for WfWindowTimePicker
function to24h(time: string): string {
  if (!time.includes("AM") && !time.includes("PM")) return time; // already 24h
  const [timePart, period] = time.trim().split(" ");
  const [hStr, mStr] = timePart.split(":");
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (period === "PM" && h !== 12) h += 12;
  if (period === "AM" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

// Converts "HH:MM" (24h) to "H:MM AM/PM" matching the timeOptions format
function to12h(time24: string): string {
  const [hStr, mStr] = time24.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  const suffix = h < 12 ? "AM" : "PM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, "0")} ${suffix}`;
}

export default function WorkflowBuilderPage() {
  const { user } = useAuth();
  const isTrialExpired =
    user?.planStatus === "trialing" &&
    !!user?.organization?.trial_ends_at &&
    new Date() > new Date(user.organization.trial_ends_at);
  const isPlanDeactivated = user?.planStatus === "cancelled" || user?.planStatus === "unpaid";
  const hasGoldPlusAccess =
    !!user?.organization?.plan_features?.campaign_features && !isTrialExpired && !isPlanDeactivated;

  const { id: workflowIdParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const segmentFromUrl = searchParams.get("segment") ?? "";

  // For new workflows, we create one on mount and store the id in state
  const [workflowId, setWorkflowId] = useState<string | undefined>(workflowIdParam ?? undefined);
  const isNewSessionRef = useRef(!workflowIdParam);
  // Tracks whether schedule fields were restored from a saved workflow so the
  // campaign pre-populate effect does not overwrite them on the initial load.
  const scheduleRestoredFromWorkflowRef = useRef(false);
  // Prevents the existingWorkflow effect from re-applying schedule state on
  // subsequent query invalidations (e.g. after saveLeadsMutation completes).
  // Without this guard the schedule tab is reset to DB values mid-edit.
  const scheduleInitializedRef = useRef(false);
  const selectionInitializedRef = useRef(false);
  const titleInitializedRef = useRef(false);
  const [workflowTitle, setWorkflowTitle] = useState("");
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filterSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mutations / queries
  const createMutation = useCreateWorkflowMutation();
  const updateMutation = useUpdateWorkflowMutation();
  const saveFiltersMutation = useSaveFiltersMutation();
  const saveLeadsMutation = useSaveLeadsMutation();
  const activateMutation = useActivateWorkflowMutation();

  // Load existing workflow when editing
  const { data: workflowData } = useWorkflowQuery(workflowId);
  const existingWorkflow = workflowData?.data;
  const wfSendingStatus = existingWorkflow ? getWorkflowSendingStatus(existingWorkflow) : null;
  const wfCanEdit = wfSendingStatus === null || wfSendingStatus === "pending";

  // Register breadcrumb title so the nav shows workflow name instead of UUID
  useRegisterBreadcrumbTitle(workflowIdParam, workflowTitle || existingWorkflow?.title || "");

  // Segments from API
  const { data: segmentsData } = useQuery({
    queryKey: ["segments-list"],
    queryFn: () => get<{ segments: Array<{ id: string; name: string; lead_count: number }> }>(API_URL.SEGMENTS.LIST),
  });
  const segmentsList = segmentsData?.data?.segments ?? [];

  // Campaigns from API — active only
  const { data: campaignsData } = useCampaignsQuery({ per_page: 100, tab: "active" });
  const campaignsList = (campaignsData?.data?.campaigns ?? []).filter((c) => c.status === "active");

  // Sequences from API — active only
  const { data: sequencesData } = useSequencesQuery({ per_page: 100, status: "active" });
  const sequencesList = (sequencesData?.data?.sequences ?? []).filter((s) => s.status === "active");

  const [currentStep, setCurrentStep] = useState(0);
  const [segment, setSegment] = useState(segmentFromUrl);
  const [filters, setFilters] = useState<FilterItem[]>([]);
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [previewPage, setPreviewPage] = useState(1);
  const segmentFromUrlSavedRef = useRef(false);

  const [campaignMode, setCampaignMode] = useState("existing");
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);

  // Fetch full campaign details from the API whenever a campaign is selected.
  // This avoids relying on the list cache which may be stale or incomplete.
  const { data: selectedCampaignDetail } = useCampaignQuery(selectedCampaignId ?? undefined);
  const selectedCampaignFull = selectedCampaignDetail?.data ?? null;

  const [newCampaignName, setNewCampaignName] = useState("");
  const [campaignGoal, setCampaignGoal] = useState("Generate Meetings");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const [sequenceMode, setSequenceMode] = useState("existing");
  const [selectedSequenceId, setSelectedSequenceId] = useState<string | null>(null);
  const [newSequenceName, setNewSequenceName] = useState("");
  const [sequenceSteps, setSequenceSteps] = useState<SequenceStep[]>([]);
  const [activeEditStep, setActiveEditStep] = useState<number | null>(null);
  const [showAddStep, setShowAddStep] = useState(false);
  const [seqConditionToggles, setSeqConditionToggles] = useState<SmartCondition[]>(
    smartConditionPresets.map((p) => ({ ...p })),
  );

  const [scheduleType, setScheduleType] = useState<"immediately" | "later">("immediately");
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [showWbDatePicker, setShowWbDatePicker] = useState(false);
  const [showWbTimePicker, setShowWbTimePicker] = useState(false);
  const [scheduleTimezone, setScheduleTimezone] = useState("");
  const [scheduleFrom, setScheduleFrom] = useState("09:00");
  const [scheduleTo, setScheduleTo] = useState("17:00");
  const [activeDays, setActiveDays] = useState<string[]>(["Mon", "Tue", "Wed", "Thu", "Fri"]);
  const [dailyLimit, setDailyLimit] = useState(50);

  const [launched, setLaunched] = useState(false);

  // ── Create Campaign Modal ────────────────────────────────────────────────
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [modalStep, setModalStep] = useState(0);
  const [modalCampaignName, setModalCampaignName] = useState("");
  const [modalGoalId, setModalGoalId] = useState("");
  const [modalSenderIds, setModalSenderIds] = useState<string[]>([]);
  const [modalSendType, setModalSendType] = useState<"immediately" | "later">("immediately");
  const [modalScheduleDate, setModalScheduleDate] = useState("");
  const [modalScheduleTime, setModalScheduleTime] = useState("");
  const [modalTimezone, setModalTimezone] = useState("");
  const [modalSendingDays, setModalSendingDays] = useState<string[]>(["Mon", "Tue", "Wed", "Thu", "Fri"]);
  const [modalWindowStart, setModalWindowStart] = useState("09:00");
  const [modalWindowEnd, setModalWindowEnd] = useState("17:00");
  const [modalDailyLimit, setModalDailyLimit] = useState("50");
  const [modalError, setModalError] = useState("");
  const [modalShowSenderPicker, setModalShowSenderPicker] = useState(false);
  const [modalShowAddSender, setModalShowAddSender] = useState(false);
  const [modalAddSenderForm, setModalAddSenderForm] = useState({
    from_name: "",
    from_email: "",
    reply_to_email: "",
    title: "",
    is_default: false,
  });
  const [modalAddSenderError, setModalAddSenderError] = useState("");
  const [modalShowDatePicker, setModalShowDatePicker] = useState(false);
  const [modalShowTimePicker, setModalShowTimePicker] = useState(false);
  const [modalShowWindowStartPicker, setModalShowWindowStartPicker] = useState(false);
  const [modalShowWindowEndPicker, setModalShowWindowEndPicker] = useState(false);
  const modalDatePickerRef = useRef<HTMLDivElement>(null);
  const modalTimePickerRef = useRef<HTMLDivElement>(null);
  const modalWindowStartRef = useRef<HTMLDivElement>(null);
  const modalWindowEndRef = useRef<HTMLDivElement>(null);
  const wbDatePickerRef = useRef<HTMLDivElement>(null);
  const wbTimePickerRef = useRef<HTMLDivElement>(null);
  const [showWbFromPicker, setShowWbFromPicker] = useState(false);
  const [showWbToPicker, setShowWbToPicker] = useState(false);
  const wbFromPickerRef = useRef<HTMLDivElement>(null);
  const wbToPickerRef = useRef<HTMLDivElement>(null);

  const { data: goalsData } = useCampaignGoalsQuery();
  const { data: sendersData } = useSenderIdentitiesQuery();
  const campaignGoals: CampaignGoal[] = (goalsData?.data as unknown as CampaignGoal[]) ?? [];
  const senderIdentities: SenderIdentity[] = (sendersData?.data as unknown as SenderIdentity[]) ?? [];
  const createCampaignMutation = useCreateCampaignMutation();
  const createSenderForModalMutation = useCreateSenderIdentityMutation();

  const modalPrimarySender = senderIdentities.find((s) => s.id === modalSenderIds[0]);
  const modalSelectedGoalName = campaignGoals.find((g) => g.id === modalGoalId)?.name ?? "";
  const modalStepValid = !!modalCampaignName.trim() && !!modalGoalId && modalSenderIds.length > 0;
  const modalCanGoNext = modalStep === 0 ? modalStepValid : modalStep === 1 ? !!modalTimezone : true;

  // Auto-select default sender when senders load
  useEffect(() => {
    if (senderIdentities.length > 0 && modalSenderIds.length === 0) {
      const def = senderIdentities.find((s) => s.is_default) ?? senderIdentities[0];
      setModalSenderIds([def.id]);
    }
  }, [senderIdentities]);

  // Close pickers on outside click
  useEffect(() => {
    if (!showCampaignModal) return;
    function handleOutside(e: MouseEvent) {
      if (modalDatePickerRef.current && !modalDatePickerRef.current.contains(e.target as Node))
        setModalShowDatePicker(false);
      if (modalTimePickerRef.current && !modalTimePickerRef.current.contains(e.target as Node))
        setModalShowTimePicker(false);
      if (modalWindowStartRef.current && !modalWindowStartRef.current.contains(e.target as Node))
        setModalShowWindowStartPicker(false);
      if (modalWindowEndRef.current && !modalWindowEndRef.current.contains(e.target as Node))
        setModalShowWindowEndPicker(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [showCampaignModal]);

  // Close workflow schedule pickers on outside click
  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (wbDatePickerRef.current && !wbDatePickerRef.current.contains(e.target as Node)) setShowWbDatePicker(false);
      if (wbTimePickerRef.current && !wbTimePickerRef.current.contains(e.target as Node)) setShowWbTimePicker(false);
      if (wbFromPickerRef.current && !wbFromPickerRef.current.contains(e.target as Node)) setShowWbFromPicker(false);
      if (wbToPickerRef.current && !wbToPickerRef.current.contains(e.target as Node)) setShowWbToPicker(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const resetCampaignModal = () => {
    setModalStep(0);
    setModalCampaignName("");
    setModalGoalId("");
    const def = senderIdentities.find((s) => s.is_default) ?? senderIdentities[0];
    setModalSenderIds(def ? [def.id] : []);
    setModalSendType("immediately");
    setModalScheduleDate("");
    setModalScheduleTime("");
    setModalTimezone("");
    setModalSendingDays(["Mon", "Tue", "Wed", "Thu", "Fri"]);
    setModalWindowStart("09:00");
    setModalWindowEnd("17:00");
    setModalDailyLimit("50");
    setModalError("");
    setModalShowSenderPicker(false);
    setModalShowAddSender(false);
    setModalAddSenderForm({ from_name: "", from_email: "", reply_to_email: "", title: "", is_default: false });
    setModalAddSenderError("");
    setModalShowDatePicker(false);
    setModalShowTimePicker(false);
    setModalShowWindowStartPicker(false);
    setModalShowWindowEndPicker(false);
  };

  const handleModalNext = () => {
    if (modalStep === 0) {
      if (!modalCampaignName.trim()) {
        setModalError("Campaign name is required.");
        return;
      }
      if (!modalGoalId) {
        setModalError("Please select a campaign goal.");
        return;
      }
      if (modalSenderIds.length === 0) {
        setModalError("Please select at least one sender profile.");
        return;
      }
      setModalError("");
      setModalStep(1);
    } else if (modalStep === 1) {
      if (modalSendType === "later" && !modalScheduleDate) {
        setModalError("Please select a date.");
        return;
      }
      setModalError("");
      setModalStep(2);
    }
  };

  const handleModalLaunch = async (status: "active" | "draft") => {
    try {
      const payload = {
        name: modalCampaignName || "Untitled Campaign",
        goal_id: modalGoalId || null,
        sender_profile_id: modalSenderIds[0] || null,
        sender_profile_ids: modalSenderIds,
        schedule_type: modalSendType === "later" ? "later" : "immediately",
        scheduled_at:
          modalSendType === "later" && modalScheduleDate ? `${modalScheduleDate}T${modalWindowStart}:00` : null,
        timezone: modalTimezone,
        sending_days: modalSendingDays,
        sending_hours_start: modalWindowStart || null,
        sending_hours_end: modalWindowEnd || null,
        daily_limit: parseInt(modalDailyLimit, 10) || null,
        status,
      };
      const res = await createCampaignMutation.mutateAsync(payload as Record<string, unknown>);
      const newId = (res as { data?: { id?: string } })?.data?.id;
      if (status === "active" && newId) {
        setSelectedCampaignId(newId);
        scheduleSave({ campaign_id: newId });
      }
      setCampaignMode("existing");
      setShowCampaignModal(false);
      resetCampaignModal();
      toast.success(status === "active" ? "Campaign created and selected!" : "Campaign saved as draft.");
    } catch (e: unknown) {
      setModalError((e as Error).message ?? "Failed to create campaign.");
    }
  };
  // ── Create Sequence Modal ─────────────────────────────────────────────────
  const [showSequenceModal, setShowSequenceModal] = useState(false);
  const [seqModalId, setSeqModalId] = useState<string | undefined>();
  const [seqModalName, setSeqModalName] = useState("");
  const [seqModalNameError, setSeqModalNameError] = useState(false);
  const [seqModalSelectedStepId, setSeqModalSelectedStepId] = useState<string | null>(null);
  const [seqModalSelectedVariantId, setSeqModalSelectedVariantId] = useState<string | null>(null);
  const [seqModalLocalSubject, setSeqModalLocalSubject] = useState("");
  const [seqModalLocalBody, setSeqModalLocalBody] = useState("");
  const [seqModalSubjectError, setSeqModalSubjectError] = useState(false);
  const [seqModalBodyError, setSeqModalBodyError] = useState(false);
  const [seqModalSaveStatus, setSeqModalSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [seqModalDelayValues, setSeqModalDelayValues] = useState<Record<string, { value: number; unit: string }>>({});
  const [seqModalEmailViewMode, setSeqModalEmailViewMode] = useState<"editor" | "preview">("editor");
  const [seqModalPreviewHtml, setSeqModalPreviewHtml] = useState<string | null>(null);
  const [seqModalPreviewLoading, setSeqModalPreviewLoading] = useState(false);
  const [seqModalNewStepType, setSeqModalNewStepType] = useState<"follow_up" | "value_add" | "break_up" | "custom">(
    "follow_up",
  );
  const [seqModalDeleteTarget, setSeqModalDeleteTarget] = useState<{ stepId: string; stepLabel: string } | null>(null);
  const seqModalSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [seqModalActiveTab, setSeqModalActiveTab] = useState<"Sequences" | "Options">("Sequences");
  const [seqModalSequenceOnReply, setSeqModalSequenceOnReply] = useState(true);
  const [seqModalBlockOnBounce, setSeqModalBlockOnBounce] = useState(false);
  const [seqModalUnsubscribeLink, setSeqModalUnsubscribeLink] = useState(true);
  const [seqModalFooterText, setSeqModalFooterText] = useState("");

  const createSeqMutation = useCreateSeqMutation();
  const updateSeqNameMutation = useUpdateSeqMutation();
  const activateSeqModalMutation = useActivateSeqMutation();
  const addSeqStepMutation = useAddStepMutation();
  const updateSeqStepMutation = useUpdateStepMutation();
  const deleteSeqStepMutation = useDeleteStepMutation();
  const addSeqVariantMutation = useAddVariantMutation();
  const updateSeqVariantMutation = useUpdateVariantMutation();
  const deleteSeqVariantMutation = useDeleteVariantMutation();

  const seqModalQuery = useSequenceQuery(seqModalId);
  const seqModalData = seqModalQuery.data?.data;
  const seqModalSteps = seqModalData?.steps ?? [];
  const seqModalCurrentStep = seqModalSteps.find((s) => s.id === seqModalSelectedStepId) ?? null;
  const seqModalCurrentVariant =
    seqModalCurrentStep?.variants.find((v) => v.id === seqModalSelectedVariantId) ??
    seqModalCurrentStep?.variants[0] ??
    null;
  const seqModalUsedTypes = new Set(seqModalSteps.slice(1).map((s) => s.step_type));
  const seqModalAvailableTypes = [
    { value: "follow_up" as const, label: "Follow Up" },
    { value: "value_add" as const, label: "Value Add" },
    { value: "break_up" as const, label: "Break Up" },
    { value: "custom" as const, label: "Custom" },
  ].filter((opt) => !seqModalUsedTypes.has(opt.value));

  // Auto-select first step when sequence data loads; sync delay values
  useEffect(() => {
    if (!seqModalData || seqModalSteps.length === 0) return;
    // Sync delay values from server (only for steps not yet tracked locally)
    setSeqModalDelayValues((prev) => {
      const next = { ...prev };
      seqModalSteps.forEach((s) => {
        if (!(s.id in next)) {
          const amount = s.delay_config?.amount ?? s.delay_config?.value;
          const rawUnit = s.delay_config?.unit;
          next[s.id] = {
            value: typeof amount === "number" ? amount : 3,
            unit:
              typeof rawUnit === "string" ? rawUnit.charAt(0).toUpperCase() + rawUnit.slice(1).toLowerCase() : "Days",
          };
        }
      });
      return next;
    });
    if (seqModalSelectedStepId && seqModalSteps.find((s) => s.id === seqModalSelectedStepId)) return;
    const firstStep = seqModalSteps[0];
    setSeqModalSelectedStepId(firstStep.id);
    const firstVariant = firstStep.variants[0];
    if (firstVariant) {
      setSeqModalSelectedVariantId(firstVariant.id);
      setSeqModalLocalSubject(firstVariant.subject_line ?? "");
      setSeqModalLocalBody(extractEmailBody(firstVariant.email_body ?? ""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seqModalData]);

  // Sync subject/body when step/variant selection changes
  useEffect(() => {
    const step = seqModalSteps.find((s) => s.id === seqModalSelectedStepId);
    const variant = step?.variants.find((v) => v.id === seqModalSelectedVariantId) ?? step?.variants[0] ?? null;
    if (variant) {
      setSeqModalLocalSubject(variant.subject_line ?? "");
      setSeqModalLocalBody(extractEmailBody(variant.email_body ?? ""));
    }
    setSeqModalSubjectError(false);
    setSeqModalBodyError(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seqModalSelectedStepId, seqModalSelectedVariantId]);

  const resetSeqModal = () => {
    setSeqModalId(undefined);
    setSeqModalName("");
    setSeqModalNameError(false);
    setSeqModalSelectedStepId(null);
    setSeqModalSelectedVariantId(null);
    setSeqModalLocalSubject("");
    setSeqModalLocalBody("");
    setSeqModalSubjectError(false);
    setSeqModalBodyError(false);
    setSeqModalSaveStatus("idle");
    setSeqModalNewStepType("follow_up");
    setSeqModalDeleteTarget(null);
    setSeqModalActiveTab("Sequences");
    setSeqModalSequenceOnReply(true);
    setSeqModalBlockOnBounce(false);
    setSeqModalUnsubscribeLink(true);
    setSeqModalFooterText("");
  };

  const handleSeqModalNameBlur = () => {
    if (!seqModalName.trim()) {
      setSeqModalNameError(true);
      return;
    }
    setSeqModalNameError(false);
    if (seqModalId) {
      updateSeqNameMutation.mutate({ id: seqModalId, data: { name: seqModalName } });
      return;
    }
    setSeqModalSaveStatus("saving");
    createSeqMutation.mutate(
      { name: seqModalName.trim() },
      {
        onSuccess: (res) => {
          const newId = (res as { data: { id: string } }).data.id;
          if (newId) setSeqModalId(newId);
          setSeqModalSaveStatus("saved");
          if (seqModalSaveTimerRef.current) clearTimeout(seqModalSaveTimerRef.current);
          seqModalSaveTimerRef.current = setTimeout(() => setSeqModalSaveStatus("idle"), 2000);
        },
        onError: () => {
          setSeqModalSaveStatus("error");
          toast.error("Failed to create sequence");
        },
      },
    );
  };

  const handleSeqModalSaveVariant = () => {
    let valid = true;
    if (!seqModalLocalSubject.trim()) {
      setSeqModalSubjectError(true);
      valid = false;
    }
    if (!bodyHasContent(seqModalLocalBody)) {
      setSeqModalBodyError(true);
      valid = false;
    }
    if (!valid) return;
    if (!seqModalCurrentStep || !seqModalCurrentVariant || !seqModalId) return;
    setSeqModalSaveStatus("saving");
    updateSeqVariantMutation.mutate(
      {
        stepId: seqModalCurrentStep.id,
        variantId: seqModalCurrentVariant.id,
        sequenceId: seqModalId,
        subject_line: seqModalLocalSubject,
        email_body: seqModalLocalBody,
      },
      {
        onSuccess: () => {
          setSeqModalSaveStatus("saved");
          if (seqModalSaveTimerRef.current) clearTimeout(seqModalSaveTimerRef.current);
          seqModalSaveTimerRef.current = setTimeout(() => setSeqModalSaveStatus("idle"), 2000);
        },
        onError: () => setSeqModalSaveStatus("error"),
      },
    );
  };

  const handleSeqModalAddStep = () => {
    if (!seqModalId) return;
    addSeqStepMutation.mutate(
      { sequenceId: seqModalId, step_type: seqModalNewStepType },
      { onError: () => toast.error("Failed to add step") },
    );
  };

  const handleSeqModalDeleteStep = () => {
    if (!seqModalDeleteTarget || !seqModalId) return;
    deleteSeqStepMutation.mutate(
      { sequenceId: seqModalId, stepId: seqModalDeleteTarget.stepId },
      {
        onSuccess: () => {
          if (seqModalSelectedStepId === seqModalDeleteTarget.stepId) {
            setSeqModalSelectedStepId(null);
            setSeqModalSelectedVariantId(null);
          }
          setSeqModalDeleteTarget(null);
          toast("Step deleted");
        },
        onError: () => {
          toast.error("Failed to delete step");
          setSeqModalDeleteTarget(null);
        },
      },
    );
  };

  const handleSeqModalAddVariant = (stepId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!seqModalId) return;
    addSeqVariantMutation.mutate(
      { stepId, sequenceId: seqModalId },
      { onError: () => toast.error("Failed to add variant") },
    );
  };

  const handleSeqModalDeleteVariant = (stepId: string, variantId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!seqModalId) return;
    deleteSeqVariantMutation.mutate(
      { stepId, variantId, sequenceId: seqModalId },
      {
        onSuccess: () => {
          if (seqModalSelectedVariantId === variantId) {
            const step = seqModalSteps.find((s) => s.id === stepId);
            const next = step?.variants.find((v) => v.id !== variantId);
            if (next) {
              setSeqModalSelectedVariantId(next.id);
              setSeqModalLocalSubject(next.subject_line ?? "");
              setSeqModalLocalBody(extractEmailBody(next.email_body ?? ""));
            }
          }
          toast("Variant deleted");
        },
        onError: () => toast.error("Failed to delete variant"),
      },
    );
  };

  const handleSeqModalSaveOptions = () => {
    if (!seqModalId) return;
    setSeqModalSaveStatus("saving");
    updateSeqNameMutation.mutate(
      {
        id: seqModalId,
        data: {
          options: {
            sequence_on_reply: seqModalSequenceOnReply,
            block_on_bounce: seqModalBlockOnBounce,
            unsubscribe_link: seqModalUnsubscribeLink,
            footer_text: seqModalFooterText,
          },
        },
      },
      {
        onSuccess: () => {
          setSeqModalSaveStatus("saved");
          if (seqModalSaveTimerRef.current) clearTimeout(seqModalSaveTimerRef.current);
          seqModalSaveTimerRef.current = setTimeout(() => setSeqModalSaveStatus("idle"), 2000);
          toast.success("Options saved");
        },
        onError: () => {
          setSeqModalSaveStatus("error");
          toast.error("Failed to save options");
        },
      },
    );
  };

  const handleSeqModalActivate = () => {
    if (!seqModalId) {
      setSeqModalNameError(true);
      toast.error("Please enter the sequence name first");
      return;
    }
    activateSeqModalMutation.mutate(seqModalId, {
      onSuccess: () => {
        setSelectedSequenceId(seqModalId);
        setSequenceMode("existing");
        scheduleSave({ sequence_id: seqModalId });
        setShowSequenceModal(false);
        resetSeqModal();
        toast.success("Sequence activated and selected!");
      },
      onError: (err: unknown) => {
        const anyErr = err as { response?: { data?: { error?: { code?: string; details?: string[] } } } };
        const errData = anyErr?.response?.data?.error;
        if (errData?.code === "VALIDATION_ERROR" && errData.details) {
          errData.details.forEach((d: string) => toast.error(d));
        } else {
          toast.error("Failed to activate sequence. Make sure all steps have subject and body saved.");
        }
      },
    });
  };

  const [openVarPicker, setOpenVarPicker] = useState<number | null>(null);
  const [varPickerCategory, setVarPickerCategory] = useState("Contact");
  const [openAiPanel, setOpenAiPanel] = useState<number | null>(null);
  const [aiGenerating, setAiGenerating] = useState<string | null>(null);

  // Auto-create workflow on mount when in "new" mode
  useEffect(() => {
    if (workflowIdParam || workflowId) return;
    createMutation.mutate(
      {},
      {
        onSuccess: (res) => {
          const newId = (res as { data?: { id?: string } })?.data?.id;
          if (newId) {
            setWorkflowId(newId);
            navigate(`/app/workflows/${newId}/edit`, { replace: true });
          }
        },
        onError: () => toast.error("Failed to create workflow. Please try again."),
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Populate state when loading an existing workflow
  useEffect(() => {
    if (!existingWorkflow) return;
    if (!titleInitializedRef.current) {
      titleInitializedRef.current = true;
      if (!isNewSessionRef.current) setWorkflowTitle(existingWorkflow.title || "");
    }
    if (!selectionInitializedRef.current) {
      selectionInitializedRef.current = true;
      if (existingWorkflow.segment_id) setSegment(existingWorkflow.segment_id);
      if (existingWorkflow.campaign_id) {
        setSelectedCampaignId(existingWorkflow.campaign_id);
        setCampaignMode("existing");
      }
      if (existingWorkflow.sequence_id) {
        setSelectedSequenceId(existingWorkflow.sequence_id);
        setSequenceMode("existing");
      }
    }
    if (existingWorkflow.filters && existingWorkflow.filters.length > 0) {
      setFilters(
        existingWorkflow.filters.map((f) => ({
          field: f.field,
          operator: f.operator,
          value: f.value,
        })),
      );
    }
    if (existingWorkflow.leads && existingWorkflow.leads.length > 0) {
      const checkedIds = existingWorkflow.leads.filter((l) => l.is_checked).map((l) => String(l.lead_provider_id));
      setSelectedLeadIds(checkedIds);
    }
    // Restore workflow-specific schedule fields only on the first load.
    // Subsequent re-fetches (e.g. triggered by saveLeadsMutation invalidating
    // the workflow query) must NOT overwrite schedule changes the user has
    // already made on the Schedule tab.
    if (!scheduleInitializedRef.current) {
      scheduleInitializedRef.current = true;
      const hasExistingSchedule = !!(
        existingWorkflow.schedule_timezone ||
        existingWorkflow.schedule_from ||
        existingWorkflow.schedule_to ||
        existingWorkflow.schedule_daily_limit != null ||
        existingWorkflow.schedule_scheduled_at
      );
      if (!isNewSessionRef.current && hasExistingSchedule) {
        scheduleRestoredFromWorkflowRef.current = true;
      }
      setScheduleType(existingWorkflow.schedule_type ?? "immediately");
      if (existingWorkflow.schedule_scheduled_at) {
        const dt = existingWorkflow.schedule_scheduled_at.slice(0, 16); // "YYYY-MM-DDTHH:mm"
        setScheduleDate(dt.slice(0, 10));
        setScheduleTime(dt.slice(11, 16));
      }
      if (existingWorkflow.schedule_from) setScheduleFrom(to24h(existingWorkflow.schedule_from));
      if (existingWorkflow.schedule_to) setScheduleTo(to24h(existingWorkflow.schedule_to));
      if (existingWorkflow.schedule_daily_limit != null) setDailyLimit(existingWorkflow.schedule_daily_limit);
      if (existingWorkflow.schedule_timezone) setScheduleTimezone(existingWorkflow.schedule_timezone);
    }
  }, [existingWorkflow]);

  // Pre-populate schedule from the selected campaign (fetched fresh from API).
  // Skipped when the campaign was set by loading an existing workflow — in that
  // case the workflow's own saved schedule values take precedence.
  useEffect(() => {
    if (!selectedCampaignFull) return;
    if (scheduleRestoredFromWorkflowRef.current) {
      scheduleRestoredFromWorkflowRef.current = false;
      const tz = selectedCampaignFull.timezone ?? "";
      if (tz && !scheduleTimezone) {
        setScheduleTimezone(tz);
        scheduleSave({ schedule_timezone: tz });
      }
      return;
    }

    // Derive all values from campaign first so we can set state AND save in one pass
    const newScheduleType = selectedCampaignFull.schedule_type ?? "immediately";
    const rawDt = selectedCampaignFull.scheduled_at ?? "";
    const newScheduleDate = rawDt ? rawDt.slice(0, 10) : "";
    const newScheduleTime = rawDt ? rawDt.slice(11, 16) : "";
    const newTimezone = selectedCampaignFull.timezone ?? "";
    const newFrom = selectedCampaignFull.sending_hours_start ?? "09:00";
    const newTo = selectedCampaignFull.sending_hours_end ?? "17:00";
    const newDailyLimit = selectedCampaignFull.daily_limit ?? 50;

    // Update local state for immediate UI display
    setScheduleType(newScheduleType);
    setScheduleDate(newScheduleDate);
    setScheduleTime(newScheduleTime);
    setScheduleTimezone(newTimezone);
    setScheduleFrom(newFrom);
    setScheduleTo(newTo);
    if (selectedCampaignFull.sending_days && selectedCampaignFull.sending_days.length > 0)
      setActiveDays(selectedCampaignFull.sending_days);
    setDailyLimit(newDailyLimit);

    // Persist to database so values survive page reload / edit mode
    const savePayload: Record<string, unknown> = {
      schedule_type: newScheduleType,
      schedule_timezone: newTimezone || null,
      schedule_from: newFrom,
      schedule_to: newTo,
      schedule_daily_limit: newDailyLimit,
    };
    if (newScheduleDate && newScheduleTime) {
      savePayload.schedule_scheduled_at = `${newScheduleDate}T${newScheduleTime}:00`;
    }
    scheduleSave(savePayload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCampaignFull]);

  // Auto-save segment_id when navigating here via ?segment= URL param.
  // Waits until workflowId is available (created on mount for new workflows).
  useEffect(() => {
    if (!workflowId || !segmentFromUrl || segmentFromUrlSavedRef.current || workflowIdParam) return;
    segmentFromUrlSavedRef.current = true;
    scheduleSave({ segment_id: segmentFromUrl });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId, segmentFromUrl]);

  // ── Lead Preview ──────────────────────────────────────────────────────────
  // The query key includes page + activeFilters so it refetches on every change.
  const activeFilters = filters.filter((f) => f.value.trim() !== "");
  // Serialise filters to a stable JSON string used in the query key and as the
  // request param.  Always produce a string (never undefined) so that clearing
  // all filters ("[]") still tells the backend to use zero filters rather than
  // falling back to the workflow's last-saved filter set.
  const filtersJson = JSON.stringify(activeFilters);

  // Reset preview page when segment or filters change
  useEffect(() => {
    setPreviewPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segment, filtersJson]);

  const {
    data: previewData,
    isLoading: previewLoading,
    isFetching: previewFetching,
  } = useQuery({
    // Include workflowId in the key so the query re-runs after the workflow is
    // first created and the ID becomes available.
    queryKey: ["segment-preview", workflowId, segment, filtersJson, previewPage],
    queryFn: () =>
      get<{ leads: PreviewLead[] }>(API_URL.WORKFLOWS.LEADS_PREVIEW(workflowId!), {
        per_page: 25,
        page: previewPage,
        // Supply segment_id when the local state has one (the endpoint also
        // falls back to workflow.segment_id on the backend if omitted).
        ...(segment ? { segment_id: segment } : {}),
        // Pass filters as a JSON string — avoids the axios 0.21.x behaviour of
        // JSON-stringifying objects inside arrays when using the native array
        // format, which would require an extra decode step on the PHP side.
        // real_time=1 tells the backend to honour this value exactly (even when
        // it is "[]") instead of falling back to the workflow's saved filters.
        filters_json: filtersJson,
        real_time: 1,
      }),
    enabled: !!workflowId,
  });

  const previewLeads: PreviewLead[] = previewData?.data?.leads ?? [];
  const previewMeta = previewData?.meta as Record<string, number> | null;
  const previewTotal: number = previewMeta?.total ?? previewLeads.length;
  const previewLastPage: number = previewMeta?.last_page ?? 1;
  // Only mark checkboxes as checked when the loaded data's page matches the requested page.
  // Without this guard, React Query serves stale page-1 data while navigating to page 2,
  // making all page-1 leads (all selected) appear checked on page 2.
  const loadedPage = previewMeta?.page ?? 0;
  const pageDataReady = loadedPage === previewPage;

  // Debounced auto-save helper
  const scheduleSave = (updateData: Record<string, unknown>) => {
    if (!workflowId) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setSaveStatus("saving");
    autoSaveTimerRef.current = setTimeout(() => {
      updateMutation.mutate(
        { id: workflowId, data: updateData },
        {
          onSuccess: () => setSaveStatus("saved"),
          onError: () => {
            setSaveStatus("error");
            toast.error("Auto-save failed. Your changes may not be saved.");
          },
        },
      );
    }, 800);
  };

  // Debounced auto-save for filters — keeps preview in sync as the user edits
  const scheduleFilterSave = (currentFilters: FilterItem[]) => {
    if (!workflowId) return;
    if (filterSaveTimerRef.current) clearTimeout(filterSaveTimerRef.current);
    filterSaveTimerRef.current = setTimeout(() => {
      saveFiltersMutation.mutate({ id: workflowId, filters: currentFilters });
    }, 800);
  };

  // Void queryClient import warning
  void queryClient;

  const insertVariable = (stepIdx: number, tag: string) => {
    setSequenceSteps((prev) =>
      prev.map((s, i) =>
        i === stepIdx ? { ...s, body: s.body + (s.body && !s.body.endsWith(" ") ? " " : "") + tag } : s,
      ),
    );
    setOpenVarPicker(null);
  };

  const generateAiContent = (stepIdx: number, templateId: string) => {
    setAiGenerating(templateId);
    setTimeout(() => {
      const output = sampleAiOutputs[templateId] || "";
      setSequenceSteps((prev) =>
        prev.map((s, i) => (i === stepIdx ? { ...s, body: s.body + (s.body ? "\n\n" : "") + output } : s)),
      );
      setAiGenerating(null);
      setOpenAiPanel(null);
    }, 1500);
  };

  const toggleLead = (id: string) => {
    setSelectedLeadIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const allCurrentPreviewPageSelected =
    pageDataReady && previewLeads.length > 0 && previewLeads.every((l) => selectedLeadIds.includes(String(l.id)));

  const toggleAllLeads = () => {
    // Don't act while the page data hasn't loaded yet — previewLeads would
    // still contain stale data from the previous page, making this a silent no-op.
    if (!pageDataReady) return;
    if (allCurrentPreviewPageSelected) {
      const currentIds = new Set(previewLeads.map((l) => String(l.id)));
      setSelectedLeadIds((prev) => prev.filter((id) => !currentIds.has(id)));
    } else {
      setSelectedLeadIds((prev) => [...new Set([...prev, ...previewLeads.map((l) => String(l.id))])]);
    }
  };

  const addFilter = () => {
    const newFilters = [...filters, { field: "Industry", operator: "is", value: "" }];
    setFilters(newFilters);
    scheduleFilterSave(newFilters);
  };

  const removeFilter = (idx: number) => {
    const newFilters = filters.filter((_, i) => i !== idx);
    setFilters(newFilters);
    scheduleFilterSave(newFilters);
  };

  const updateFilter = (idx: number, key: string, val: string) => {
    const newFilters = filters.map((f, i) => (i === idx ? { ...f, [key]: val } : f));
    setFilters(newFilters);
    scheduleFilterSave(newFilters);
  };

  const addStepFromTemplate = (template: StepTemplate) => {
    if (sequenceSteps.length >= 7) return;
    const newStep: SequenceStep = {
      id: sequenceSteps.length + 1,
      type: template.id,
      subject: template.subject,
      body: template.body,
      delay: sequenceSteps.length === 0 ? 0 : template.delay,
      color: template.color,
      label: template.label,
    };
    setSequenceSteps((prev) => [...prev, newStep]);
    setShowAddStep(false);
    setActiveEditStep(sequenceSteps.length);
  };

  const removeSequenceStep = (idx: number) => {
    setSequenceSteps((prev) => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, id: i + 1 })));
    if (activeEditStep === idx) setActiveEditStep(null);
    else if (activeEditStep !== null && activeEditStep > idx) setActiveEditStep(activeEditStep - 1);
  };

  const updateSequenceStep = (idx: number, key: string, val: string | number) => {
    setSequenceSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, [key]: val } : s)));
  };

  const toggleSmartCondition = (presetIdx: number) => {
    setSeqConditionToggles((prev) => prev.map((p, i) => (i === presetIdx ? { ...p, enabled: !p.enabled } : p)));
  };

  const moveStep = (idx: number, direction: number) => {
    if ((direction === -1 && idx === 0) || (direction === 1 && idx === sequenceSteps.length - 1)) return;
    const newSteps = [...sequenceSteps];
    const temp = newSteps[idx];
    newSteps[idx] = newSteps[idx + direction];
    newSteps[idx + direction] = temp;
    newSteps.forEach((s, i) => {
      s.id = i + 1;
      if (i === 0) s.delay = 0;
      else if (s.delay === 0) s.delay = 3;
    });
    setSequenceSteps(newSteps);
    if (activeEditStep === idx) setActiveEditStep(idx + direction);
  };

  const getSelectedCampaign = () => campaignsList.find((c) => c.id === selectedCampaignId);
  const getSelectedSequence = () => sequencesList.find((s) => s.id === selectedSequenceId);

  const getCampaignName = () => {
    if (campaignMode === "existing") return getSelectedCampaign()?.name || "No campaign selected";
    return newCampaignName || "New campaign";
  };

  const getSequenceName = () => {
    if (sequenceMode === "existing") return getSelectedSequence()?.name || "No sequence selected";
    return newSequenceName || "New sequence";
  };

  const getStepCount = () => {
    if (sequenceMode === "existing") return getSelectedSequence()?.steps?.length || 0;
    return sequenceSteps.length;
  };

  const parseTimeMins = (hhmm: string): number => {
    const [h, m] = (hhmm || "00:00").split(":").map(Number);
    return h * 60 + m;
  };

  const getDelayMins = (dc: Record<string, unknown> | null): number => {
    if (!dc) return 0;
    const amount = (dc.amount as number) ?? 0;
    const unit = ((dc.unit as string) ?? "days").toLowerCase();
    return unit === "hours" ? amount * 60 : amount * 24 * 60;
  };

  const pushIntoWindowAbs = (abs: number, fromMins: number, toMins: number): number => {
    const DAY = 1440;
    const local = abs % DAY;
    const dayIdx = Math.floor(abs / DAY);
    if (local < fromMins) return dayIdx * DAY + fromMins;
    if (local > toMins) return (dayIdx + 1) * DAY + fromMins;
    return abs;
  };

  const simulateWorkflow = (
    steps: import("@/hooks/useSequences").SequenceStep[],
    leads: number,
    limit: number,
    from: string,
    to: string,
  ): { stepStartDays: number[]; estimatedDays: number; dayMap: Map<number, Map<number, number>> } => {
    const DAY = 1440;
    const fromMins = parseTimeMins(from || "09:00");
    const toMins = parseTimeMins(to || "17:00");

    if (!steps.length || leads <= 0) return { stepStartDays: [], estimatedDays: 1, dayMap: new Map() };

    const effectiveLimit = limit > 0 ? limit : 9999;
    let cursor = fromMins;
    let dailyCount = 0;
    let prevStepStartAbs = fromMins;
    const stepStartDays: number[] = [];
    const dayMap = new Map<number, Map<number, number>>();

    const advanceToNextDay = () => {
      const dayIdx = Math.floor(cursor / DAY);
      cursor = (dayIdx + 1) * DAY + fromMins;
      dailyCount = 0;
    };

    for (let i = 0; i < steps.length; i++) {
      const emailsInStep = leads * Math.max(steps[i].variants?.length || 1, 1);

      if (i > 0) {
        const gapMins = getDelayMins(steps[i].delay_config);
        const desired = pushIntoWindowAbs(prevStepStartAbs + gapMins, fromMins, toMins);

        if (desired > cursor) {
          if (Math.floor(desired / DAY) !== Math.floor(cursor / DAY)) dailyCount = 0;
          cursor = desired;
        } else {
          const sep = pushIntoWindowAbs(cursor + 1, fromMins, toMins);
          if (Math.floor(sep / DAY) !== Math.floor(cursor / DAY)) dailyCount = 0;
          cursor = sep;
        }
      }

      prevStepStartAbs = cursor;
      stepStartDays.push(Math.floor(cursor / DAY) + 1);

      for (let e = 0; e < emailsInStep; e++) {
        if (dailyCount >= effectiveLimit || cursor % DAY > toMins) advanceToNextDay();
        const currentDay = Math.floor(cursor / DAY) + 1;
        if (!dayMap.has(currentDay)) dayMap.set(currentDay, new Map());
        const stepMap = dayMap.get(currentDay)!;
        stepMap.set(i, (stepMap.get(i) ?? 0) + 1);
        dailyCount++;
        if (e < emailsInStep - 1) cursor++;
      }
    }

    return { stepStartDays, estimatedDays: Math.floor(cursor / DAY) + 1, dayMap };
  };

  const getTimelineRows = (): TimelineDay[] => {
    const buildRows = (dm: Map<number, Map<number, number>>, labels: string[]): TimelineDay[] =>
      [...dm.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([day, stepCounts]) => ({
          day,
          entries: [...stepCounts.entries()]
            .sort((a, b) => a[0] - b[0])
            .map(([stepIdx, count]) => ({
              stepNum: stepIdx + 1,
              label: labels[stepIdx] ?? "Follow-up",
              emailCount: count,
            })),
        }));

    if (sequenceMode === "existing") {
      const seq = getSelectedSequence();
      if (!seq || !seq.steps?.length) return [];
      const { dayMap } = simulateWorkflow(seq.steps, reviewLeadCount, dailyLimit, scheduleFrom, scheduleTo);
      const labels = seq.steps.map((_, i) => (i === 0 ? "Initial email" : "Follow-up"));
      return buildRows(dayMap, labels);
    }

    // New-sequence mode: simulate using sequenceSteps (delay field is in days, 1 variant each)
    if (!sequenceSteps.length || reviewLeadCount <= 0) return [];
    const DAY2 = 1440;
    const fromMins2 = parseTimeMins(scheduleFrom || "09:00");
    const toMins2 = parseTimeMins(scheduleTo || "17:00");
    const effLimit = dailyLimit > 0 ? dailyLimit : 9999;
    const dm = new Map<number, Map<number, number>>();
    let cur = fromMins2;
    let dc = 0;
    let prevAbs = fromMins2;

    const advDay = () => {
      const di = Math.floor(cur / DAY2);
      cur = (di + 1) * DAY2 + fromMins2;
      dc = 0;
    };

    for (let i = 0; i < sequenceSteps.length; i++) {
      if (i > 0) {
        const gapMins = sequenceSteps[i].delay * 24 * 60;
        const desired = pushIntoWindowAbs(prevAbs + gapMins, fromMins2, toMins2);
        if (desired > cur) {
          if (Math.floor(desired / DAY2) !== Math.floor(cur / DAY2)) dc = 0;
          cur = desired;
        } else {
          const sep = pushIntoWindowAbs(cur + 1, fromMins2, toMins2);
          if (Math.floor(sep / DAY2) !== Math.floor(cur / DAY2)) dc = 0;
          cur = sep;
        }
      }
      prevAbs = cur;
      for (let e = 0; e < reviewLeadCount; e++) {
        if (dc >= effLimit || cur % DAY2 > toMins2) advDay();
        const d = Math.floor(cur / DAY2) + 1;
        if (!dm.has(d)) dm.set(d, new Map());
        const sm = dm.get(d)!;
        sm.set(i, (sm.get(i) ?? 0) + 1);
        dc++;
        if (e < reviewLeadCount - 1) cur++;
      }
    }

    const labels = sequenceSteps.map((_, i) => (i === 0 ? "Initial email" : "Follow-up"));
    return buildRows(dm, labels);
  };

  const insertMergeVar = (v: string) => {
    setBody((prev) => prev + " " + v);
  };

  // ── Review helpers ──────────────────────────────────────────────────────────
  const getSegmentName = () => segmentsList.find((s) => s.id === segment)?.name || segment || "No segment selected";

  const getIntervalDescription = () => {
    if (sequenceMode === "existing") {
      const seq = getSelectedSequence();
      if (!seq || !seq.steps || seq.steps.length <= 1) return "Single step";
      const labels = seq.steps
        .slice(1)
        .map((s) => {
          const dc = s.delay_config as { amount?: number; unit?: string } | null;
          if (!dc) return null;
          const unit = (dc.unit ?? "days").toLowerCase();
          const label = unit === "hours" ? "hour" : "day";
          return `${dc.amount}-${label}`;
        })
        .filter(Boolean) as string[];
      if (labels.length === 0) return "Single step";
      const unique = [...new Set(labels)];
      return unique.length === 1 ? `${unique[0]} intervals` : `${unique[0]}–${unique[unique.length - 1]} intervals`;
    }
    if (sequenceSteps.length <= 1) return "Single step";
    const delays = sequenceSteps.slice(1).map((s) => s.delay);
    const min = Math.min(...delays);
    const max = Math.max(...delays);
    return min === max ? `${min}-day intervals` : `${min}–${max} day intervals`;
  };

  const reviewLeadCount = selectedLeadIds.length || previewTotal;
  const estimatedOpens = Math.round(reviewLeadCount * 0.35);
  const estimatedReplies = Math.round(estimatedOpens * 0.08);
  const estimatedOpenPct = reviewLeadCount > 0 ? "35.0" : "0.0";
  const estimatedReplyPct = reviewLeadCount > 0 ? ((estimatedReplies / reviewLeadCount) * 100).toFixed(1) : "0.0";
  const timelineRows = getTimelineRows();
  const estimatedDays = (() => {
    if (sequenceMode === "existing") {
      const seq = getSelectedSequence();
      if (!seq || !seq.steps?.length) return 1;
      return simulateWorkflow(seq.steps, reviewLeadCount, dailyLimit, scheduleFrom, scheduleTo).estimatedDays;
    }
    return timelineRows.length > 0 ? timelineRows[timelineRows.length - 1].day : 1;
  })();

  return (
    <div className="wb-page">
      <div className="wb-back-row">
        <Link to="/app/workflows" className="wb-back-link">
          <ArrowLeft size={16} /> Back to Workflows
        </Link>
        {saveStatus === "saving" && <span className="wb-save-status wb-save-status--saving">Saving…</span>}
        {saveStatus === "saved" && <span className="wb-save-status wb-save-status--saved">Saved</span>}
        {saveStatus === "error" && <span className="wb-save-status wb-save-status--error">Save failed</span>}
      </div>
      <h1 className="wb-title">Workflow Builder</h1>

      <div className="wb-stepper">
        {stepLabels.map((label, i) => {
          const Icon = stepIcons[i];
          const isActive = i === currentStep;
          const isComplete = i < currentStep;
          return (
            <div key={label} className="wb-stepper-item">
              <div className="wb-stepper-step">
                <div
                  className={`wb-stepper-circle ${isComplete ? "wb-stepper-circle--done" : isActive ? "wb-stepper-circle--active" : ""}`}
                >
                  {isComplete ? <CheckCircle size={18} /> : <Icon size={18} />}
                </div>
                <span className={`wb-stepper-label ${isActive ? "wb-stepper-label--active" : ""}`}>{label}</span>
              </div>
              {i < stepLabels.length - 1 && (
                <div className={`wb-stepper-connector ${isComplete ? "wb-stepper-connector--done" : ""}`} />
              )}
            </div>
          );
        })}
      </div>

      <div className="wb-nav-bar wb-nav-bar--top">
        <button
          className="btn btn-secondary"
          onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
          style={{ visibility: currentStep === 0 ? "hidden" : "visible" }}
        >
          <ArrowLeft size={16} /> Back
        </button>
        {currentStep < 4 && (
          <button
            className="btn btn-primary"
            disabled={
              (currentStep === 0 && (!segment || selectedLeadIds.length === 0)) ||
              (currentStep === 1 && !selectedCampaignId) ||
              (currentStep === 2 && !selectedSequenceId) ||
              (currentStep === 3 && !scheduleTimezone)
            }
            onClick={() => {
              if (workflowId) {
                if (currentStep === 0) {
                  saveFiltersMutation.mutate({ id: workflowId, filters });
                  const leadsPayload = selectedLeadIds.map((id) => ({
                    lead_provider_id: id,
                    is_checked: true,
                  }));
                  if (leadsPayload.length > 0) {
                    saveLeadsMutation.mutate({ id: workflowId, leads: leadsPayload });
                  }
                }
                if (currentStep === 1 && selectedCampaignId) {
                  scheduleSave({ campaign_id: selectedCampaignId });
                }
                if (currentStep === 2 && selectedSequenceId) {
                  scheduleSave({ sequence_id: selectedSequenceId });
                }
              }
              setCurrentStep(Math.min(4, currentStep + 1));
            }}
          >
            Next <ArrowRight size={16} />
          </button>
        )}
      </div>

      {currentStep === 0 && (
        <div className="card wb-card">
          <div className="wb-form-group">
            <label className="wb-label">Workflow Title</label>
            <input
              type="text"
              className="input-field"
              placeholder="Enter workflow title..."
              value={workflowTitle}
              onChange={(e) => {
                const val = e.target.value;
                setWorkflowTitle(val);
                if (!val.trim()) {
                  // User cleared the field — cancel any pending stale save
                  if (autoSaveTimerRef.current) {
                    clearTimeout(autoSaveTimerRef.current);
                    autoSaveTimerRef.current = null;
                  }
                  setSaveStatus("idle");
                } else {
                  scheduleSave({ title: val.trim() });
                }
              }}
            />
          </div>
          <h2 className="wb-card-title">Build Your Audience</h2>

          <div className="wb-form-group">
            <label className="wb-label">Segment</label>
            <div className="wb-select-wrap">
              <select
                className="input-field wb-select"
                value={segment}
                onChange={(e) => {
                  setSegment(e.target.value);
                  scheduleSave({ segment_id: e.target.value || null });
                }}
              >
                <option value="">Choose a segment...</option>
                {segmentsList.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              <ChevronDown size={18} className="wb-select-icon" />
            </div>
          </div>

          <div className="wb-form-group">
            <label className="wb-label">Additional Filters</label>
            {filters.map((f, i) => (
              <div key={i} className="wb-filter-row">
                <select
                  className="input-field wb-filter-field"
                  value={f.field}
                  onChange={(e) => updateFilter(i, "field", e.target.value)}
                >
                  {filterFields.map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
                <select
                  className="input-field wb-filter-op"
                  value={f.operator}
                  onChange={(e) => updateFilter(i, "operator", e.target.value)}
                >
                  {filterOperators.map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
                <input
                  className="input-field wb-filter-val"
                  value={f.value}
                  onChange={(e) => updateFilter(i, "value", e.target.value)}
                />
                <button className="wb-filter-remove" onClick={() => removeFilter(i)}>
                  <X size={18} />
                </button>
              </div>
            ))}
            <button className="btn btn-ghost btn-sm" onClick={addFilter}>
              <Plus size={14} /> Add Filter
            </button>
          </div>

          <div className="wb-form-group">
            {segment ? (
              <>
                <label className="wb-label">Lead Preview</label>
                <div
                  className="wb-table-wrap"
                  style={{ opacity: previewFetching ? 0.6 : 1, transition: "opacity 0.2s" }}
                >
                  <table className="wb-table">
                    <thead>
                      <tr>
                        <th className="wb-th wb-inline-w40">
                          <input
                            type="checkbox"
                            checked={allCurrentPreviewPageSelected}
                            onChange={toggleAllLeads}
                            className="wb-checkbox"
                          />
                        </th>
                        <th className="wb-th">Name</th>
                        <th className="wb-th">Company</th>
                        <th className="wb-th">Industry</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewLoading ? (
                        <tr>
                          <td colSpan={4} className="wb-td wb-inline-center-muted">
                            Loading leads…
                          </td>
                        </tr>
                      ) : previewLeads.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="wb-td wb-inline-center-muted">
                            No leads match the selected segment and filters.
                          </td>
                        </tr>
                      ) : (
                        previewLeads.map((lead) => (
                          <tr
                            key={lead.id}
                            className={
                              pageDataReady && selectedLeadIds.includes(String(lead.id)) ? "wb-row--selected" : ""
                            }
                          >
                            <td className="wb-td">
                              <input
                                type="checkbox"
                                checked={pageDataReady && selectedLeadIds.includes(String(lead.id))}
                                onChange={() => toggleLead(String(lead.id))}
                                className="wb-checkbox"
                              />
                            </td>
                            <td className="wb-td">
                              <div className="wb-lead-name-cell">
                                <div className="wb-avatar">
                                  {lead.name
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")}
                                </div>
                                <span className="wb-lead-name">{lead.name}</span>
                              </div>
                            </td>
                            <td className="wb-td wb-td-muted">{lead.company}</td>
                            <td className="wb-td">
                              <span className="wb-industry-badge">{lead.industry}</span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="wb-preview-footer">
                  <p className="wb-showing-text">
                    {previewFetching && !previewLoading
                      ? "Updating…"
                      : `Showing ${previewLeads.length === 0 ? 0 : (previewPage - 1) * 25 + 1}–${Math.min(previewPage * 25, previewTotal)} of ${previewTotal.toLocaleString()} leads`}
                  </p>
                  {previewLastPage > 1 && (
                    <div className="wb-pag">
                      <button
                        className="wb-pag-btn wb-pag-btn--arrow"
                        onClick={() => setPreviewPage((p) => Math.max(1, p - 1))}
                        disabled={previewPage === 1}
                      >
                        <ChevronLeft size={15} />
                      </button>
                      {previewPage > 2 && (
                        <button className="wb-pag-btn" onClick={() => setPreviewPage(1)}>
                          1
                        </button>
                      )}
                      {previewPage > 3 && <span className="wb-pag-ellipsis">…</span>}
                      {previewPage > 1 && (
                        <button className="wb-pag-btn" onClick={() => setPreviewPage(previewPage - 1)}>
                          {previewPage - 1}
                        </button>
                      )}
                      <button className="wb-pag-btn wb-pag-btn--active">{previewPage}</button>
                      {previewPage < previewLastPage && (
                        <button className="wb-pag-btn" onClick={() => setPreviewPage(previewPage + 1)}>
                          {previewPage + 1}
                        </button>
                      )}
                      {previewPage < previewLastPage - 2 && <span className="wb-pag-ellipsis">…</span>}
                      {previewPage < previewLastPage - 1 && (
                        <button className="wb-pag-btn" onClick={() => setPreviewPage(previewLastPage)}>
                          {previewLastPage}
                        </button>
                      )}
                      <button
                        className="wb-pag-btn wb-pag-btn--arrow"
                        onClick={() => setPreviewPage((p) => Math.min(previewLastPage, p + 1))}
                        disabled={previewPage === previewLastPage}
                      >
                        <ChevronRight size={15} />
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>

          {(!segment || selectedLeadIds.length === 0) && (
            <p className="wb-inline-label-hint">{!segment ? "" : "Select at least one lead to continue."}</p>
          )}

          <div className="wb-audience-chip">
            <Users size={18} />
            <span>Estimated audience: {selectedLeadIds.length.toLocaleString()} leads</span>
          </div>
        </div>
      )}

      {currentStep === 1 && (
        <div className="card wb-card">
          <h2 className="wb-card-title">Configure Campaign</h2>

          <div className="wb-mode-cards">
            {hasGoldPlusAccess && (
              <div
                className={`wb-mode-card ${campaignMode === "existing" ? "wb-mode-card--active" : ""}`}
                onClick={() => setCampaignMode("existing")}
              >
                <FileText size={24} color="var(--sky)" />
                <div>
                  <p className="wb-mode-card-title">Use Existing Campaign</p>
                  <p className="wb-mode-card-desc">Select from your active campaigns</p>
                </div>
              </div>
            )}
            <div
              className="wb-mode-card"
              onClick={() => {
                setCampaignMode("existing");
                setShowCampaignModal(true);
              }}
            >
              <Plus size={24} color="var(--sky)" />
              <div>
                <p className="wb-mode-card-title">Create New Campaign</p>
                <p className="wb-mode-card-desc">Build a new campaign from scratch</p>
              </div>
            </div>
          </div>

          <div className="wb-existing-list">
            {(hasGoldPlusAccess ? campaignsList : campaignsList.filter((c) => c.id === selectedCampaignId)).map((c) => {
              const missingProfile = c.sender_profile === null;
              return (
                <div
                  key={c.id}
                  className={`wb-selectable-card ${selectedCampaignId === c.id ? "wb-selectable-card--active" : ""} ${missingProfile ? "wb-selectable-card--disabled" : ""}`}
                  onClick={() => {
                    if (!missingProfile) setSelectedCampaignId(c.id);
                  }}
                  title={
                    missingProfile
                      ? "No sender profile attached — configure one in Campaign settings before using this campaign."
                      : undefined
                  }
                >
                  <p className="wb-selectable-name">{c.name}</p>
                  <p className="wb-selectable-meta">{missingProfile ? "⚠ No sender profile" : c.status}</p>
                </div>
              );
            })}
            {(hasGoldPlusAccess ? campaignsList : campaignsList.filter((c) => c.id === selectedCampaignId)).length ===
              0 && <p className="wb-selectable-empty">No active campaigns found. Create one first.</p>}
          </div>

          {!selectedCampaignId && <p className="wb-audience-chip">Select or create a campaign to continue.</p>}
        </div>
      )}

      {currentStep === 2 && (
        <div className="card wb-card">
          <h2 className="wb-card-title">Set Up Sequence</h2>

          <div className="wb-mode-cards">
            {hasGoldPlusAccess && (
              <div
                className={`wb-mode-card ${sequenceMode === "existing" ? "wb-mode-card--active" : ""}`}
                onClick={() => setSequenceMode("existing")}
              >
                <GitBranch size={24} color="var(--sky)" />
                <div>
                  <p className="wb-mode-card-title">Use Existing Sequence</p>
                  <p className="wb-mode-card-desc">Select from your saved sequences</p>
                </div>
              </div>
            )}
            <div
              className="wb-mode-card"
              onClick={() => {
                setSequenceMode("existing");
                setShowSequenceModal(true);
              }}
            >
              <Plus size={24} color="var(--sky)" />
              <div>
                <p className="wb-mode-card-title">Create New Sequence</p>
                <p className="wb-mode-card-desc">Build a custom sequence</p>
              </div>
            </div>
          </div>

          <div className="wb-existing-list">
            {(hasGoldPlusAccess ? sequencesList : sequencesList.filter((s) => s.id === selectedSequenceId)).map((s) => (
              <div
                key={s.id}
                className={`wb-selectable-card ${selectedSequenceId === s.id ? "wb-selectable-card--active" : ""}`}
                onClick={() => setSelectedSequenceId(s.id)}
              >
                <p className="wb-selectable-name">{s.name}</p>
                <p className="wb-selectable-meta">{s.steps?.length ?? 0} steps</p>
              </div>
            ))}
            {(hasGoldPlusAccess ? sequencesList : sequencesList.filter((s) => s.id === selectedSequenceId)).length ===
              0 && <p className="wb-selectable-empty">No active sequences found. Create one first.</p>}
          </div>

          {!selectedSequenceId && <p className="wb-audience-chip">Select or create a sequence to continue.</p>}

          {sequenceMode === "new" && (
            <div className="wb-new-form">
              <div className="wb-form-group">
                <label className="wb-label">Sequence Name</label>
                <input
                  className="input-field"
                  placeholder="Enter sequence name..."
                  value={newSequenceName}
                  onChange={(e) => setNewSequenceName(e.target.value)}
                />
              </div>

              {sequenceSteps.length === 0 ? (
                <div className="wb-empty-steps">
                  <div className="wb-empty-steps-icon">
                    <GitBranch size={32} color="var(--gray-300)" />
                  </div>
                  <h3 className="wb-empty-steps-title">Build your sequence</h3>
                  <p className="wb-empty-steps-desc">
                    Choose a template to get started, or create a custom step from scratch.
                  </p>
                  <div className="wb-template-grid">
                    {stepTemplates.map((t) => {
                      const Icon = t.icon;
                      return (
                        <button key={t.id} className="wb-template-card" onClick={() => addStepFromTemplate(t)}>
                          <div className="wb-template-icon" style={{ background: t.color + "18", color: t.color }}>
                            <Icon size={20} />
                          </div>
                          <span className="wb-template-name">{t.label}</span>
                          <span className="wb-template-desc">{t.desc}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="wb-form-group">
                  <label className="wb-label">Steps</label>
                  <div className="wb-timeline">
                    {(hasGoldPlusAccess ? sequenceSteps : sequenceSteps.slice(0, 1)).map((step, i) => {
                      const isEditing = activeEditStep === i;
                      return (
                        <div key={step.id} className="wb-tl-item">
                          <div className="wb-tl-connector-wrap">
                            <div className="wb-tl-dot" style={{ background: step.color || "var(--sky)" }}>
                              {step.id}
                            </div>
                            {i < sequenceSteps.length - 1 && (
                              <div className="wb-tl-line">
                                {i < sequenceSteps.length - 1 && sequenceSteps[i + 1]?.delay > 0 && (
                                  <span className="wb-tl-delay-badge">
                                    <Clock size={10} /> {sequenceSteps[i + 1].delay}d
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="wb-tl-content">
                            {!isEditing ? (
                              <div className="wb-tl-card" onClick={() => setActiveEditStep(i)}>
                                <div className="wb-tl-card-header">
                                  <div className="wb-tl-card-type" style={{ color: step.color || "var(--sky)" }}>
                                    {step.label || "Custom Step"}
                                  </div>
                                  <div className="wb-tl-card-actions">
                                    {i > 0 && (
                                      <button
                                        className="wb-tl-action-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          moveStep(i, -1);
                                        }}
                                        title="Move up"
                                      >
                                        <ArrowLeft size={14} className="wb-inline-rotate-90" />
                                      </button>
                                    )}
                                    {i < sequenceSteps.length - 1 && (
                                      <button
                                        className="wb-tl-action-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          moveStep(i, 1);
                                        }}
                                        title="Move down"
                                      >
                                        <ArrowLeft size={14} className="wb-inline-rotate-neg90" />
                                      </button>
                                    )}
                                    <button
                                      className="wb-tl-action-btn wb-tl-action-btn--danger"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeSequenceStep(i);
                                      }}
                                      title="Remove"
                                    >
                                      <X size={14} />
                                    </button>
                                  </div>
                                </div>
                                <p className="wb-tl-card-subject">{step.subject || "No subject set"}</p>
                                <p className="wb-tl-card-preview">
                                  {step.body
                                    ? step.body.substring(0, 80) + (step.body.length > 80 ? "..." : "")
                                    : "Click to add content"}
                                </p>
                                <div className="wb-tl-card-footer">
                                  {i === 0 ? (
                                    <span className="wb-tl-chip">
                                      <Send size={11} /> Sent immediately
                                    </span>
                                  ) : (
                                    <span className="wb-tl-chip">
                                      <Clock size={11} /> After {step.delay} {step.delay === 1 ? "day" : "days"}
                                    </span>
                                  )}
                                  {step.body && step.body.includes("{{ai_") && (
                                    <span className="wb-tl-chip wb-tl-chip--ai">
                                      <Sparkles size={11} /> AI personalized
                                    </span>
                                  )}
                                  <span className="wb-tl-edit-hint">Click to edit</span>
                                </div>
                              </div>
                            ) : (
                              <div className="wb-tl-editor">
                                <div className="wb-tl-editor-header">
                                  <h4 className="wb-tl-editor-title">Editing: {step.label || "Custom Step"}</h4>
                                  <button className="wb-tl-done-btn" onClick={() => setActiveEditStep(null)}>
                                    <CheckCircle size={14} /> Done
                                  </button>
                                </div>

                                {i > 0 && (
                                  <div className="wb-tl-editor-timing">
                                    <Clock size={14} color="var(--gray-500)" />
                                    <span>Send this step</span>
                                    <select
                                      className="input-field wb-seq-delay-select"
                                      value={step.delay}
                                      onChange={(e) => updateSequenceStep(i, "delay", parseInt(e.target.value))}
                                    >
                                      {Array.from({ length: 14 }, (_, n) => n + 1).map((d) => (
                                        <option key={d} value={d}>
                                          {d} {d === 1 ? "day" : "days"}
                                        </option>
                                      ))}
                                    </select>
                                    <span>after the previous step</span>
                                  </div>
                                )}

                                <div className="wb-tl-editor-field">
                                  <label className="wb-label">Subject Line</label>
                                  <input
                                    className="input-field"
                                    placeholder="Subject line..."
                                    value={step.subject}
                                    onChange={(e) => updateSequenceStep(i, "subject", e.target.value)}
                                  />
                                </div>

                                <div className="wb-tl-editor-field">
                                  <div className="wb-seq-body-toolbar">
                                    <span className="wb-seq-body-label">Email Content</span>
                                    <div className="wb-seq-toolbar-actions">
                                      <div className="wb-var-picker-wrap">
                                        <button
                                          className={`wb-toolbar-btn ${openVarPicker === i ? "wb-toolbar-btn--active" : ""}`}
                                          onClick={() => {
                                            setOpenVarPicker(openVarPicker === i ? null : i);
                                            setOpenAiPanel(null);
                                            setVarPickerCategory("Contact");
                                          }}
                                        >
                                          <Tag size={14} /> Variables
                                        </button>
                                        {openVarPicker === i && (
                                          <div className="wb-var-dropdown">
                                            <div className="wb-var-cats">
                                              {variableCategories.map((cat) => {
                                                const CatIcon = catIcons[cat.icon];
                                                return (
                                                  <button
                                                    key={cat.label}
                                                    className={`wb-var-cat-btn ${varPickerCategory === cat.label ? "wb-var-cat-btn--active" : ""}`}
                                                    onClick={() => setVarPickerCategory(cat.label)}
                                                  >
                                                    <CatIcon size={14} /> {cat.label}
                                                  </button>
                                                );
                                              })}
                                            </div>
                                            <div className="wb-var-list">
                                              {variableCategories
                                                .find((c) => c.label === varPickerCategory)
                                                ?.vars.map((v) => (
                                                  <button
                                                    key={v.tag}
                                                    className="wb-var-item"
                                                    onClick={() => insertVariable(i, v.tag)}
                                                  >
                                                    <span className="wb-var-item-label">{v.label}</span>
                                                    <code className="wb-var-item-tag">{v.tag}</code>
                                                  </button>
                                                ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                      <div className="wb-ai-wrap">
                                        <button
                                          className={`wb-toolbar-btn wb-toolbar-btn--ai ${openAiPanel === i ? "wb-toolbar-btn--active" : ""}`}
                                          onClick={() => {
                                            setOpenAiPanel(openAiPanel === i ? null : i);
                                            setOpenVarPicker(null);
                                          }}
                                        >
                                          <Wand2 size={14} /> AI Content
                                        </button>
                                        {openAiPanel === i && (
                                          <div className="wb-ai-dropdown">
                                            <div className="wb-ai-dropdown-header">
                                              <Sparkles size={16} color="var(--amber)" />
                                              <span>Generate AI-Personalized Content</span>
                                            </div>
                                            <p className="wb-ai-dropdown-desc">
                                              AI creates unique content per recipient based on their profile and
                                              business type.
                                            </p>
                                            <div className="wb-ai-templates">
                                              {aiTemplates.map((t) => (
                                                <button
                                                  key={t.id}
                                                  className="wb-ai-template-btn"
                                                  onClick={() => generateAiContent(i, t.id)}
                                                  disabled={aiGenerating !== null}
                                                >
                                                  <div className="wb-ai-template-top">
                                                    <Sparkles size={14} color="var(--amber)" />
                                                    <span className="wb-ai-template-label">{t.label}</span>
                                                    {aiGenerating === t.id && <Loader2 size={14} className="wb-spin" />}
                                                  </div>
                                                  <p className="wb-ai-template-desc">{t.desc}</p>
                                                </button>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <textarea
                                    className="textarea-field wb-seq-textarea"
                                    rows={8}
                                    placeholder="Write your email content..."
                                    value={step.body}
                                    onChange={(e) => updateSequenceStep(i, "body", e.target.value)}
                                  />
                                  {step.body && step.body.includes("{{ai_") && (
                                    <div className="wb-ai-notice">
                                      <Sparkles size={13} color="var(--amber)" />
                                      <span>AI variables generate unique content per recipient</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {hasGoldPlusAccess && !showAddStep && sequenceSteps.length < 7 && (
                    <button className="wb-add-step-trigger" onClick={() => setShowAddStep(true)}>
                      <Plus size={16} /> Add Another Step
                    </button>
                  )}

                  {hasGoldPlusAccess && showAddStep && (
                    <div className="wb-add-step-panel">
                      <div className="wb-add-step-header">
                        <span>Choose a step type</span>
                        <button className="wb-tl-action-btn" onClick={() => setShowAddStep(false)}>
                          <X size={14} />
                        </button>
                      </div>
                      <div className="wb-template-row">
                        {stepTemplates.map((t) => {
                          const Icon = t.icon;
                          return (
                            <button
                              key={t.id}
                              className="wb-template-chip"
                              onClick={() => addStepFromTemplate(t)}
                              style={{ "--tc": t.color } as React.CSSProperties}
                            >
                              <Icon size={14} /> {t.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="wb-smart-cond-section">
                <div className="wb-smart-cond-header">
                  <GitFork size={16} color="var(--sky)" />
                  <div>
                    <h3 className="wb-smart-cond-title">Smart Response Handling</h3>
                    <p className="wb-smart-cond-desc">Toggle what happens when leads engage with your sequence</p>
                  </div>
                </div>
                <div className="wb-smart-cond-list">
                  {seqConditionToggles.map((preset, pi) => {
                    const TIcon = preset.icon;
                    return (
                      <div
                        key={preset.id}
                        className={`wb-smart-cond-item ${preset.enabled ? "wb-smart-cond-item--on" : ""}`}
                      >
                        <div className="wb-smart-cond-left">
                          <TIcon size={16} color={preset.enabled ? preset.color : "var(--gray-400)"} />
                          <span className="wb-smart-cond-label">{preset.label}</span>
                        </div>
                        <button
                          className={`wb-toggle ${preset.enabled ? "wb-toggle--on" : ""}`}
                          onClick={() => toggleSmartCondition(pi)}
                        >
                          <span className="wb-toggle-knob" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {currentStep === 3 && (
        <div className="card wb-card">
          <h2 className="wb-card-title">Schedule</h2>

          {/* Two equal-column option cards */}
          <div className="wb-sched-options-row">
            {/* Left col: Send Immediately */}
            <label className={`wb-sched-option ${scheduleType === "immediately" ? "wb-sched-option--active" : ""}`}>
              <input
                type="radio"
                name="wb-sendtype"
                value="immediately"
                checked={scheduleType === "immediately"}
                onChange={() => {
                  setScheduleType("immediately");
                  scheduleSave({ schedule_type: "immediately" });
                }}
              />
              <div>
                <p className="wb-sched-option-title">Send Immediately</p>
                <p className="wb-sched-option-desc">Start sending emails as soon as the workflow is launched</p>
              </div>
            </label>

            {/* Right col: Schedule for Later */}
            <label className={`wb-sched-option ${scheduleType === "later" ? "wb-sched-option--active" : ""}`}>
              <input
                type="radio"
                name="wb-sendtype"
                value="later"
                checked={scheduleType === "later"}
                onChange={() => {
                  setScheduleType("later");
                  scheduleSave({ schedule_type: "later" });
                }}
              />
              <div>
                <p className="wb-sched-option-title">Schedule for Later</p>
                <p className="wb-sched-option-desc">Choose a specific date to start sending</p>
              </div>
            </label>
          </div>

          {/* Timezone + Date picker in one row */}
          <div className="wb-schedule-row">
            <div className="wb-form-group wb-schedule-field">
              <label className="wb-label">
                Timezone <span style={{ color: "var(--red, #ef4444)" }}>*</span>
              </label>
              <div className="wb-select-wrap">
                <select
                  className="input-field wb-select"
                  value={scheduleTimezone}
                  onChange={(e) => {
                    setScheduleTimezone(e.target.value);
                    scheduleSave({ schedule_timezone: e.target.value });
                  }}
                >
                  <option value="">Select timezone…</option>
                  {WF_TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
                <ChevronDown size={16} className="wb-select-icon" />
              </div>
            </div>

            {scheduleType === "later" && (
              <div className="wb-form-group wb-schedule-field">
                <label className="wb-label">Start Date</label>
                <div className="cbp-picker-field" ref={wbDatePickerRef}>
                  <button
                    type="button"
                    className="cbp-picker-trigger"
                    onClick={() => {
                      setShowWbDatePicker((v) => !v);
                      setShowWbTimePicker(false);
                    }}
                  >
                    <Calendar size={16} color="var(--sky)" />
                    <span
                      className={`cbp-picker-text ${scheduleDate ? "cbp-picker-text--filled" : "cbp-picker-text--empty"}`}
                    >
                      {scheduleDate
                        ? format(parse(scheduleDate, "yyyy-MM-dd", new Date()), "MM/dd/yyyy")
                        : "Pick a date"}
                    </span>
                    <ChevronDown size={16} color="var(--gray-400)" />
                  </button>
                  {showWbDatePicker && (
                    <div className="cbp-picker-popover">
                      <DayPicker
                        mode="single"
                        selected={scheduleDate ? parse(scheduleDate, "yyyy-MM-dd", new Date()) : undefined}
                        onSelect={(day) => {
                          if (day && isValid(day)) {
                            const newDate = format(day, "yyyy-MM-dd");
                            setScheduleDate(newDate);
                            setShowWbDatePicker(false);
                            scheduleSave({
                              schedule_scheduled_at: `${newDate}T09:00:00`,
                            });
                          }
                        }}
                        disabled={{ before: new Date() }}
                        showOutsideDays
                        classNames={{
                          root: "cbp-rdp",
                          nav: "cbp-rdp-nav",
                          month_caption: "cbp-rdp-caption",
                          caption_label: "cbp-rdp-caption-label",
                          button_previous: "cbp-rdp-nav-btn cbp-rdp-nav-prev",
                          button_next: "cbp-rdp-nav-btn cbp-rdp-nav-next",
                          weekday: "cbp-rdp-weekday",
                          day: "cbp-rdp-day",
                          day_button: "cbp-rdp-day-btn",
                          selected: "cbp-rdp-selected",
                          today: "cbp-rdp-today",
                          disabled: "cbp-rdp-disabled",
                          outside: "cbp-rdp-outside",
                        }}
                        components={{
                          PreviousMonthButton: (props) => (
                            <button {...props} className="cbp-rdp-nav-btn cbp-rdp-nav-prev">
                              <ArrowLeft size={14} />
                            </button>
                          ),
                          NextMonthButton: (props) => (
                            <button {...props} className="cbp-rdp-nav-btn cbp-rdp-nav-next">
                              <ArrowRight size={14} />
                            </button>
                          ),
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Send Window */}
          <div className="wb-schedule-row">
            <div className="form-group cbp-picker-field" ref={wbFromPickerRef}>
              <label className="wb-label">Send Window From</label>
              <button
                type="button"
                className="cbp-picker-trigger"
                onClick={() => {
                  setShowWbFromPicker((v) => !v);
                  setShowWbToPicker(false);
                }}
              >
                <Clock size={16} color="var(--sky)" />
                <span className="cbp-picker-text cbp-picker-text--filled">{fmt24hDisplay(scheduleFrom)}</span>
                <ChevronDown size={16} color="var(--gray-400)" />
              </button>
              {showWbFromPicker && (
                <WfWindowTimePicker
                  value={scheduleFrom}
                  onChange={(v) => {
                    setScheduleFrom(v);
                    scheduleSave({ schedule_from: v });
                  }}
                  onClose={() => setShowWbFromPicker(false)}
                />
              )}
            </div>
            <div className="form-group cbp-picker-field" ref={wbToPickerRef}>
              <label className="wb-label">To</label>
              <button
                type="button"
                className="cbp-picker-trigger"
                onClick={() => {
                  setShowWbToPicker((v) => !v);
                  setShowWbFromPicker(false);
                }}
              >
                <Clock size={16} color="var(--sky)" />
                <span className="cbp-picker-text cbp-picker-text--filled">{fmt24hDisplay(scheduleTo)}</span>
                <ChevronDown size={16} color="var(--gray-400)" />
              </button>
              {showWbToPicker && (
                <WfWindowTimePicker
                  value={scheduleTo}
                  onChange={(v) => {
                    setScheduleTo(v);
                    scheduleSave({ schedule_to: v });
                  }}
                  onClose={() => setShowWbToPicker(false)}
                />
              )}
            </div>
          </div>

          <div className="wb-form-group">
            <label className="wb-label">Daily Limit</label>
            <input
              className="input-field wb-daily-limit"
              type="number"
              value={dailyLimit}
              onChange={(e) => {
                const val = parseInt(e.target.value) || 0;
                setDailyLimit(val);
                scheduleSave({ schedule_daily_limit: val });
              }}
            />
          </div>
        </div>
      )}

      {currentStep === 4 && (
        <div className="card wb-card">
          <h2 className="wb-card-title">Review Your Workflow</h2>

          {launched && (
            <div className="wb-success-banner">
              <CheckCircle size={20} />
              <div>
                <p className="wb-success-text">
                  Workflow launched successfully! Your sequence will begin sending shortly.
                </p>
                <Link to="/app/dashboard" className="wb-success-link">
                  View in Dashboard →
                </Link>
              </div>
            </div>
          )}

          <div className="wb-summary-cards">
            <div className="wb-summary-card wb-summary-card--sky">
              <Users size={24} />
              <p className="wb-summary-value">
                {reviewLeadCount.toLocaleString()} lead{reviewLeadCount !== 1 ? "s" : ""}
              </p>
              <p className="wb-summary-detail">{getSegmentName()}</p>
              <p className="wb-summary-detail">
                {activeFilters.length} active filter{activeFilters.length !== 1 ? "s" : ""}
              </p>
            </div>
            <div className="wb-summary-card wb-summary-card--amber">
              <FileText size={24} />
              <p className="wb-summary-value">{getCampaignName()}</p>
              <p className="wb-summary-detail wb-inline-capitalize">{getSelectedCampaign()?.status || "Campaign"}</p>
              <p className="wb-summary-detail">
                {subject ? (subject.length > 40 ? subject.substring(0, 40) + "…" : subject) : "No subject set"}
              </p>
            </div>
            <div className="wb-summary-card wb-summary-card--green">
              <GitBranch size={24} />
              <p className="wb-summary-value">{getSequenceName()}</p>
              <p className="wb-summary-detail">
                {getStepCount()} step{getStepCount() !== 1 ? "s" : ""}
              </p>
              <p className="wb-summary-detail">{getIntervalDescription()}</p>
            </div>
          </div>

          <div className="wb-timeline-section">
            <h3 className="wb-section-title">Timeline Preview</h3>
            <div className="wb-timeline">
              {timelineRows.map((td, di) => (
                <div key={di} className="wb-timeline-item">
                  <div className="wb-timeline-dot" />
                  {di < timelineRows.length - 1 && <div className="wb-timeline-line" />}
                  <div className="wb-timeline-content">
                    <span className="wb-timeline-day">Day {td.day}</span>
                    {td.entries.map((entry, ei) => (
                      <span key={ei} className="wb-timeline-step">
                        Step {entry.stepNum} – {entry.label}: {entry.emailCount} email
                        {entry.emailCount !== 1 ? "s" : ""}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="wb-estimated-section">
            <h3 className="wb-section-title">Estimated Results</h3>
            <p className="wb-estimated-sub">Based on your audience and sequence settings</p>
            <div className="wb-estimated-row">
              <div className="wb-estimated-stat">
                <Eye size={18} color="var(--sky)" />
                <div>
                  <p className="wb-estimated-label">Estimated Opens</p>
                  <p className="wb-estimated-value">
                    {estimatedOpens.toLocaleString()} ({estimatedOpenPct}%)
                  </p>
                </div>
              </div>
              <div className="wb-estimated-stat">
                <MessageSquare size={18} color="var(--green)" />
                <div>
                  <p className="wb-estimated-label">Estimated Replies</p>
                  <p className="wb-estimated-value">
                    {estimatedReplies.toLocaleString()} ({estimatedReplyPct}%)
                  </p>
                </div>
              </div>
              <div className="wb-estimated-stat">
                <Calendar size={18} color="var(--amber)" />
                <div>
                  <p className="wb-estimated-label">Estimated Duration</p>
                  <p className="wb-estimated-value">
                    {estimatedDays} day{estimatedDays !== 1 ? "s" : ""}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="wb-launch-btns">
            {!wfCanEdit && (
              <p className="wb-sending-notice">Emails are already sending — this workflow can no longer be edited.</p>
            )}
            {wfCanEdit && (
              <button
                className="btn btn-secondary"
                onClick={async () => {
                  // Cancel any pending debounce timers
                  if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
                  if (filterSaveTimerRef.current) clearTimeout(filterSaveTimerRef.current);
                  if (workflowId) {
                    await updateMutation
                      .mutateAsync({
                        id: workflowId,
                        data: {
                          title: workflowTitle || undefined,
                          segment_id: segment || null,
                          campaign_id: selectedCampaignId || null,
                          sequence_id: selectedSequenceId || null,
                        },
                      })
                      .catch(() => {});
                    await saveFiltersMutation
                      .mutateAsync({
                        id: workflowId,
                        filters: activeFilters,
                      })
                      .catch(() => {});
                  }
                  toast.success("Draft saved", { description: "Your workflow has been saved as a draft." });
                  navigate("/app/workflows");
                }}
              >
                Save as Draft
              </button>
            )}
            {wfCanEdit &&
              (existingWorkflow?.status === "active" ? (
                <button
                  className="btn wb-launch-btn"
                  onClick={async () => {
                    if (!workflowId) return;
                    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
                    if (filterSaveTimerRef.current) clearTimeout(filterSaveTimerRef.current);
                    await updateMutation
                      .mutateAsync({
                        id: workflowId,
                        data: {
                          title: workflowTitle || undefined,
                          segment_id: segment || null,
                          campaign_id: selectedCampaignId || null,
                          sequence_id: selectedSequenceId || null,
                        },
                      })
                      .catch(() => {});
                    await saveFiltersMutation
                      .mutateAsync({
                        id: workflowId,
                        filters: activeFilters,
                      })
                      .catch(() => {});
                    const leadsPayload = selectedLeadIds.map((id) => ({
                      lead_provider_id: id,
                      is_checked: true,
                    }));
                    if (leadsPayload.length > 0) {
                      await saveLeadsMutation.mutateAsync({ id: workflowId, leads: leadsPayload }).catch(() => {});
                    }
                    toast.success("Workflow saved successfully.");
                    navigate("/app/workflows");
                  }}
                  disabled={updateMutation.isPending || saveFiltersMutation.isPending}
                >
                  <CheckCircle2 size={16} /> {updateMutation.isPending ? "Saving…" : "Save Workflow"}
                </button>
              ) : (
                <button
                  className="btn wb-launch-btn"
                  onClick={async () => {
                    if (!workflowId) {
                      toast.error("Workflow not ready. Please wait a moment and try again.");
                      return;
                    }
                    // Cancel any pending debounce timers and do a final flush save
                    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
                    if (filterSaveTimerRef.current) clearTimeout(filterSaveTimerRef.current);
                    await updateMutation
                      .mutateAsync({
                        id: workflowId,
                        data: {
                          title: workflowTitle || undefined,
                          segment_id: segment || null,
                          campaign_id: selectedCampaignId || null,
                          sequence_id: selectedSequenceId || null,
                        },
                      })
                      .catch(() => {});
                    await saveFiltersMutation
                      .mutateAsync({
                        id: workflowId,
                        filters: activeFilters,
                      })
                      .catch(() => {});
                    // Save leads so the backend's checked-lead validation passes
                    const leadsPayload = selectedLeadIds.map((id) => ({
                      lead_provider_id: id,
                      is_checked: true,
                    }));
                    if (leadsPayload.length > 0) {
                      await saveLeadsMutation.mutateAsync({ id: workflowId, leads: leadsPayload }).catch(() => {});
                    }
                    activateMutation.mutate(workflowId, {
                      onSuccess: () => {
                        setLaunched(true);
                        toast.success("Workflow launched!", {
                          description: "Your sequence will begin sending shortly.",
                        });
                        navigate("/app/workflows");
                      },
                      onError: (err: unknown) => {
                        // apiClient throws an enriched Error — details live on .apiError.details
                        const apiErr = (err as { apiError?: { details?: string[] } }).apiError;
                        const details = apiErr?.details;
                        const message =
                          details && details.length > 0
                            ? details.join(" ")
                            : (err as Error).message || "Failed to launch workflow. Please try again.";
                        toast.error("Cannot launch workflow", { description: message });
                      },
                    });
                  }}
                  disabled={launched || activateMutation.isPending || updateMutation.isPending}
                >
                  <Rocket size={16} /> {activateMutation.isPending ? "Launching…" : "Launch Workflow"}
                </button>
              ))}
          </div>
        </div>
      )}

      <div className="wb-nav-bar">
        <button
          className="btn btn-secondary"
          onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
          style={{ visibility: currentStep === 0 ? "hidden" : "visible" }}
        >
          <ArrowLeft size={16} /> Back
        </button>
        {currentStep < 4 && (
          <button
            className="btn btn-primary"
            disabled={
              (currentStep === 0 && (!segment || selectedLeadIds.length === 0)) ||
              (currentStep === 1 && !selectedCampaignId) ||
              (currentStep === 2 && !selectedSequenceId) ||
              (currentStep === 3 && !scheduleTimezone)
            }
            onClick={() => {
              if (workflowId) {
                if (currentStep === 0) {
                  saveFiltersMutation.mutate({ id: workflowId, filters });
                  const leadsPayload = selectedLeadIds.map((id) => ({
                    lead_provider_id: id,
                    is_checked: true,
                  }));
                  if (leadsPayload.length > 0) {
                    saveLeadsMutation.mutate({ id: workflowId, leads: leadsPayload });
                  }
                }
                if (currentStep === 1 && selectedCampaignId) {
                  scheduleSave({ campaign_id: selectedCampaignId });
                }
                if (currentStep === 2 && selectedSequenceId) {
                  scheduleSave({ sequence_id: selectedSequenceId! });
                }
              }
              setCurrentStep(Math.min(4, currentStep + 1));
            }}
          >
            Next <ArrowRight size={16} />
          </button>
        )}
      </div>

      {/* ── Create Campaign Modal ──────────────────────────────────────────── */}
      {showCampaignModal && (
        <div className="wcm-overlay">
          <div className="wcm-modal">
            {/* Modal header (close button only) */}
            <div className="wcm-header">
              <h2 className="cbp-page-title wb-inline-title-lg">Campaign Builder</h2>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setShowCampaignModal(false);
                  resetCampaignModal();
                }}
              >
                <X size={16} />
              </button>
            </div>

            {/* cbp-steps */}
            <div className="cbp-steps wb-inline-modal-header">
              {["Campaign Setup", "Schedule", "Review"].map((label, i) => {
                const StepIcon = [FileText, Clock, CheckCircle][i];
                const isActive = i === modalStep;
                const isComplete = i < modalStep;
                return (
                  <div key={label} className="cbp-step-item">
                    <div className="cbp-step-inner">
                      <div
                        className={`cbp-step-icon ${isComplete ? "cbp-step-icon--complete" : isActive ? "cbp-step-icon--active" : "cbp-step-icon--inactive"}`}
                        onClick={() => isComplete && setModalStep(i)}
                        style={{ cursor: isComplete ? "pointer" : "default" }}
                      >
                        {isComplete ? <CheckCircle size={18} /> : <StepIcon size={18} />}
                      </div>
                      <span
                        className={`cbp-step-label ${isActive ? "cbp-step-label--active" : "cbp-step-label--inactive"}`}
                      >
                        {label}
                      </span>
                    </div>
                    {i < 2 && (
                      <div
                        className={`cbp-step-line ${isComplete ? "cbp-step-line--complete" : "cbp-step-line--incomplete"}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Top nav */}
            <div className="cbp-nav wb-inline-modal-sub-header">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setModalStep((s) => Math.max(0, s - 1));
                  setModalError("");
                }}
                style={{ visibility: modalStep === 0 ? "hidden" : "visible" }}
              >
                <ArrowLeft size={16} /> Previous
              </button>
              {modalStep < 2 && (
                <button className="btn btn-primary" onClick={handleModalNext} disabled={!modalCanGoNext}>
                  Next <ArrowRight size={16} />
                </button>
              )}
            </div>

            {/* Scrollable body */}
            <div className="wcm-body">
              {/* ── Step 0: Campaign Setup ── */}
              {modalStep === 0 && (
                <div className="card wb-inline-card-bare">
                  <h2 className="cbp-card-title">Campaign Setup</h2>

                  <div className="form-group">
                    <label className="label">Campaign Name</label>
                    <input
                      className="input-field"
                      placeholder="Enter campaign name..."
                      value={modalCampaignName}
                      onChange={(e) => {
                        setModalCampaignName(e.target.value);
                        setModalError("");
                      }}
                      autoFocus
                    />
                  </div>

                  <div className="form-group">
                    <label className="label">Campaign Goal</label>
                    <div className="cbp-goal-wrap">
                      {campaignGoals.map((g) => (
                        <button
                          key={g.id}
                          className={`cbp-goal-btn${modalGoalId === g.id ? " cbp-goal-btn--active" : ""}`}
                          onClick={() => {
                            setModalGoalId(g.id);
                            setModalError("");
                          }}
                        >
                          {g.name}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="label">Sender Profile</label>
                    {senderIdentities.length === 0 ? (
                      <div className="cbp-sender-empty-state">
                        <div className="cbp-sender-empty-icon">
                          <Send size={20} color="var(--gray-400)" />
                        </div>
                        <p className="cbp-sender-empty-title">No sender profiles yet</p>
                        <p className="cbp-sender-empty-text">
                          You need at least one sender identity to launch a campaign.
                        </p>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => navigate("/app/settings?tab=Email+Setup")}
                        >
                          <Plus size={14} /> Add Sender Profile
                        </button>
                      </div>
                    ) : modalSenderIds.length > 0 ? (
                      <div className="cbp-sender-selected-card">
                        <div className="cbp-sender-avatar">
                          {modalSenderIds.length === 1 && modalPrimarySender
                            ? modalPrimarySender.from_name
                                .split(" ")
                                .map((n) => n[0])
                                .join("")
                                .toUpperCase()
                                .slice(0, 2)
                            : modalSenderIds.length.toString()}
                        </div>
                        <div className="cbp-sender-info">
                          {modalSenderIds.length === 1 && modalPrimarySender ? (
                            <>
                              <div className="cbp-sender-name">{modalPrimarySender.from_name}</div>
                              <div className="cbp-sender-email-text">{modalPrimarySender.from_email}</div>
                            </>
                          ) : (
                            <div className="cbp-sender-name">{modalSenderIds.length} senders selected</div>
                          )}
                        </div>
                        <button className="btn btn-ghost btn-sm" onClick={() => setModalShowSenderPicker(true)}>
                          Change
                        </button>
                      </div>
                    ) : (
                      <button className="btn btn-secondary btn-sm" onClick={() => setModalShowSenderPicker(true)}>
                        Select Sender
                      </button>
                    )}
                  </div>

                  <div className="cbp-info-banner">
                    <Sparkles size={14} color="var(--amber)" />
                    <span>
                      Email content is managed in your sequences. Use the Workflow Builder for the full flow, or create
                      a sequence from the Sequences page.
                    </span>
                  </div>

                  {modalError && <p className="cbp-form-error">{modalError}</p>}
                </div>
              )}

              {/* ── Step 1: Schedule & Delivery ── */}
              {modalStep === 1 && (
                <div className="card wb-inline-card-bare">
                  <h2 className="cbp-card-title">Schedule &amp; Delivery</h2>

                  {/* Two equal-column option cards */}
                  <div className="cbp-send-options">
                    {/* Left col: Send Immediately */}
                    <label
                      className={`cbp-send-option${modalSendType === "immediately" ? " cbp-send-option--active" : ""}`}
                    >
                      <input
                        type="radio"
                        name="modal-sendtype"
                        value="immediately"
                        checked={modalSendType === "immediately"}
                        onChange={() => setModalSendType("immediately")}
                      />
                      <div>
                        <p className="cbp-send-option-title">Send Immediately</p>
                        <p className="cbp-send-option-desc">Start sending emails as soon as the campaign is launched</p>
                      </div>
                    </label>

                    {/* Right col: Schedule for Later */}
                    <label className={`cbp-send-option${modalSendType === "later" ? " cbp-send-option--active" : ""}`}>
                      <input
                        type="radio"
                        name="modal-sendtype"
                        value="later"
                        checked={modalSendType === "later"}
                        onChange={() => setModalSendType("later")}
                      />
                      <div>
                        <p className="cbp-send-option-title">Schedule for Later</p>
                        <p className="cbp-send-option-desc">Choose a specific date to start sending</p>
                      </div>
                    </label>
                  </div>

                  {/* Timezone + Date picker in one row — timezone always visible */}
                  <div className="cbp-tz-date-row">
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="label">
                        Timezone <span style={{ color: "var(--red, #ef4444)" }}>*</span>
                      </label>
                      <div className="cbp-select-wrap">
                        <select
                          className="input-field"
                          value={modalTimezone}
                          onChange={(e) => setModalTimezone(e.target.value)}
                        >
                          <option value="" disabled>
                            Select timezone…
                          </option>
                          {WF_TIMEZONES.map((tz) => (
                            <option key={tz} value={tz}>
                              {tz}
                            </option>
                          ))}
                        </select>
                        <ChevronDown size={18} className="cbp-select-chevron" />
                      </div>
                    </div>

                    {modalSendType === "later" && (
                      <div className="form-group cbp-picker-field" ref={modalDatePickerRef} style={{ marginBottom: 0 }}>
                        <label className="label">Start Date</label>
                        <button
                          type="button"
                          className="cbp-picker-trigger"
                          onClick={() => {
                            setModalShowDatePicker((v) => !v);
                            setModalShowTimePicker(false);
                          }}
                        >
                          <Calendar size={16} color="var(--sky)" />
                          <span
                            className={`cbp-picker-text ${modalScheduleDate ? "cbp-picker-text--filled" : "cbp-picker-text--empty"}`}
                          >
                            {modalScheduleDate
                              ? format(parse(modalScheduleDate, "yyyy-MM-dd", new Date()), "EEE, MMM d, yyyy")
                              : "Pick a date"}
                          </span>
                          <ChevronDown size={16} color="var(--gray-400)" />
                        </button>
                        {modalShowDatePicker && (
                          <div className="cbp-picker-popover">
                            <DayPicker
                              mode="single"
                              selected={
                                modalScheduleDate ? parse(modalScheduleDate, "yyyy-MM-dd", new Date()) : undefined
                              }
                              onSelect={(day) => {
                                if (day && isValid(day)) {
                                  setModalScheduleDate(format(day, "yyyy-MM-dd"));
                                  setModalShowDatePicker(false);
                                  setModalError("");
                                }
                              }}
                              disabled={{ before: new Date() }}
                              showOutsideDays
                              classNames={{
                                root: "cbp-rdp",
                                nav: "cbp-rdp-nav",
                                month_caption: "cbp-rdp-caption",
                                caption_label: "cbp-rdp-caption-label",
                                button_previous: "cbp-rdp-nav-btn cbp-rdp-nav-prev",
                                button_next: "cbp-rdp-nav-btn cbp-rdp-nav-next",
                                weekday: "cbp-rdp-weekday",
                                day: "cbp-rdp-day",
                                day_button: "cbp-rdp-day-btn",
                                selected: "cbp-rdp-selected",
                                today: "cbp-rdp-today",
                                disabled: "cbp-rdp-disabled",
                                outside: "cbp-rdp-outside",
                              }}
                              components={{
                                PreviousMonthButton: (props) => (
                                  <button {...props} className="cbp-rdp-nav-btn cbp-rdp-nav-prev">
                                    <ArrowLeft size={14} />
                                  </button>
                                ),
                                NextMonthButton: (props) => (
                                  <button {...props} className="cbp-rdp-nav-btn cbp-rdp-nav-next">
                                    <ArrowRight size={14} />
                                  </button>
                                ),
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <h3 className="cbp-section-title">Sending Window</h3>
                  <div className="cbp-window-grid">
                    <div className="form-group cbp-picker-field" ref={modalWindowStartRef}>
                      <label className="label">Start Time</label>
                      <button
                        type="button"
                        className="cbp-picker-trigger"
                        onClick={() => {
                          setModalShowWindowStartPicker((v) => !v);
                          setModalShowWindowEndPicker(false);
                        }}
                      >
                        <Clock size={16} color="var(--sky)" />
                        <span className="cbp-picker-text cbp-picker-text--filled">
                          {(() => {
                            const [h, m] = modalWindowStart.split(":").map(Number);
                            return `${String(h % 12 || 12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
                          })()}
                        </span>
                        <ChevronDown size={16} color="var(--gray-400)" />
                      </button>
                      {modalShowWindowStartPicker && (
                        <WfWindowTimePicker
                          value={modalWindowStart}
                          onChange={(v) => setModalWindowStart(v)}
                          onClose={() => setModalShowWindowStartPicker(false)}
                        />
                      )}
                    </div>
                    <div className="form-group cbp-picker-field" ref={modalWindowEndRef}>
                      <label className="label">End Time</label>
                      <button
                        type="button"
                        className="cbp-picker-trigger"
                        onClick={() => {
                          setModalShowWindowEndPicker((v) => !v);
                          setModalShowWindowStartPicker(false);
                        }}
                      >
                        <Clock size={16} color="var(--sky)" />
                        <span className="cbp-picker-text cbp-picker-text--filled">
                          {(() => {
                            const [h, m] = modalWindowEnd.split(":").map(Number);
                            return `${String(h % 12 || 12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
                          })()}
                        </span>
                        <ChevronDown size={16} color="var(--gray-400)" />
                      </button>
                      {modalShowWindowEndPicker && (
                        <WfWindowTimePicker
                          value={modalWindowEnd}
                          onChange={(v) => setModalWindowEnd(v)}
                          onClose={() => setModalShowWindowEndPicker(false)}
                        />
                      )}
                    </div>
                  </div>

                  <h3 className="cbp-section-title">Daily Sending Limit</h3>
                  <div className="form-group">
                    <label className="label">Max Emails Per Day</label>
                    <input
                      className="input-field cbp-throttle"
                      type="number"
                      value={modalDailyLimit}
                      min={1}
                      max={10000}
                      onChange={(e) => setModalDailyLimit(e.target.value)}
                    />
                  </div>

                  {modalError && <p className="cbp-form-error">{modalError}</p>}
                </div>
              )}

              {/* ── Step 2: Review ── */}
              {modalStep === 2 && (
                <div className="card wb-inline-card-bare">
                  <h2 className="cbp-card-title">Review Campaign</h2>
                  <div className="cbp-review-grid">
                    <div className="cbp-review-card">
                      <p className="cbp-review-card-label">Campaign</p>
                      <p className="cbp-review-card-value">{modalCampaignName || "Untitled Campaign"}</p>
                      <p className="cbp-review-card-sub">Goal: {modalSelectedGoalName || "—"}</p>
                    </div>
                    <div className="cbp-review-card">
                      <p className="cbp-review-card-label">Sender</p>
                      {modalSenderIds.length > 1 ? (
                        <p className="cbp-review-card-value">{modalSenderIds.length} senders</p>
                      ) : (
                        <>
                          <p className="cbp-review-card-value">{modalPrimarySender?.from_name ?? "—"}</p>
                          <p className="cbp-review-card-sub">{modalPrimarySender?.from_email ?? ""}</p>
                        </>
                      )}
                    </div>
                    <div className="cbp-review-card">
                      <p className="cbp-review-card-label">Schedule</p>
                      <p className="cbp-review-card-value">
                        {modalSendType === "immediately" ? "Send Immediately" : "Scheduled"}
                      </p>
                      {modalSendType === "later" && modalScheduleDate ? (
                        <>
                          <p className="cbp-review-card-sub">
                            {format(parse(modalScheduleDate, "yyyy-MM-dd", new Date()), "MM/dd/yyyy")}
                          </p>
                          <p className="cbp-review-card-sub">
                            {`${fmt24hDisplay(modalWindowStart)} – ${fmt24hDisplay(modalWindowEnd)}`}
                          </p>
                        </>
                      ) : (
                        <p className="cbp-review-card-sub">
                          {`Window: ${fmt24hDisplay(modalWindowStart)} – ${fmt24hDisplay(modalWindowEnd)}`}
                        </p>
                      )}
                    </div>
                    <div className="cbp-review-card">
                      <p className="cbp-review-card-label">Daily Limit</p>
                      <p className="cbp-review-card-value">{modalDailyLimit} emails/day</p>
                    </div>
                  </div>

                  <div className="cbp-launch-actions">
                    <button
                      className="btn btn-primary btn-lg"
                      onClick={() => handleModalLaunch("active")}
                      disabled={createCampaignMutation.isPending}
                    >
                      {modalSendType === "immediately" ? (
                        <>
                          <Send size={18} /> Launch Campaign
                        </>
                      ) : (
                        <>
                          <Calendar size={18} /> Schedule Campaign
                        </>
                      )}
                    </button>
                  </div>

                  {modalError && <p className="cbp-form-error">{modalError}</p>}
                </div>
              )}
            </div>

            {/* Bottom nav */}
            <div className="cbp-nav cbp-nav--bottom wb-inline-modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setModalStep((s) => Math.max(0, s - 1));
                  setModalError("");
                }}
                style={{ visibility: modalStep === 0 ? "hidden" : "visible" }}
              >
                <ArrowLeft size={16} /> Previous
              </button>
              {modalStep < 2 && (
                <button className="btn btn-primary" onClick={handleModalNext} disabled={!modalCanGoNext}>
                  Next <ArrowRight size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Sender picker overlay — multi-select */}
          {modalShowSenderPicker && (
            <div className="cbp-sender-overlay" onClick={() => setModalShowSenderPicker(false)}>
              <div className="cbp-sender-modal" onClick={(e) => e.stopPropagation()}>
                <div className="cbp-modal-header">
                  <div>
                    <h3 className="cbp-modal-title">Choose Sender Profiles</h3>
                    <p className="cbp-modal-subtitle">
                      Select one or more senders. Emails fill the first profile to its daily cap before moving to the
                      next.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setModalShowSenderPicker(false)}
                  >
                    <X size={16} />
                  </button>
                </div>
                <div className="cbp-sender-list">
                  {senderIdentities.length === 0 ? (
                    <div className="cbp-list-placeholder">
                      <div className="cbp-list-placeholder-title">No sender profiles found</div>
                      <div className="cbp-list-placeholder-text">
                        Add a sender profile in settings to use it for this campaign.
                      </div>
                    </div>
                  ) : (
                    senderIdentities.map((s) => {
                      const selected = modalSenderIds.includes(s.id);
                      return (
                        <label
                          key={s.id}
                          className={`cbp-sender-option sp-sender-row${selected ? " cbp-sender-option--selected" : ""}`}
                          style={{ cursor: "pointer" }}
                        >
                          <input
                            type="checkbox"
                            className="sp-checkbox"
                            checked={selected}
                            onChange={() => {
                              setModalSenderIds((prev) =>
                                selected ? prev.filter((x) => x !== s.id) : [...prev, s.id],
                              );
                              setModalError("");
                            }}
                          />
                          <div className="cbp-sender-option-avatar">
                            {s.from_name
                              .split(" ")
                              .map((n) => n[0])
                              .join("")
                              .toUpperCase()
                              .slice(0, 2)}
                          </div>
                          <div className="cbp-sender-option-info">
                            <div className="cbp-sender-option-name">{s.from_name}</div>
                            <div className="cbp-sender-option-email">{s.from_email}</div>
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
                {modalSenderIds.length > 0 && (
                  <div style={{ padding: "8px 16px", fontSize: "13px", color: "var(--gray-500)" }}>
                    {modalSenderIds.length} selected
                  </div>
                )}
                <div className="wb-inline-divider-top">
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      setModalShowSenderPicker(false);
                      setModalAddSenderForm({
                        from_name: "",
                        from_email: "",
                        reply_to_email: "",
                        title: "",
                        is_default: false,
                      });
                      setModalAddSenderError("");
                      setModalShowAddSender(true);
                    }}
                  >
                    <Plus size={14} /> Add New Sender
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => setModalShowSenderPicker(false)}
                    style={{ marginLeft: "auto" }}
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add sender modal */}
          {modalShowAddSender && (
            <div className="cbp-sender-overlay" onClick={() => setModalShowAddSender(false)}>
              <div className="cbp-sender-modal" onClick={(e) => e.stopPropagation()}>
                <h3 className="cbp-modal-form-title">Add Sender Identity</h3>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    setModalAddSenderError("");
                    if (!modalAddSenderForm.from_name.trim()) {
                      setModalAddSenderError("From name is required.");
                      return;
                    }
                    if (!modalAddSenderForm.from_email.trim()) {
                      setModalAddSenderError("From email is required.");
                      return;
                    }
                    createSenderForModalMutation.mutate(
                      {
                        from_name: modalAddSenderForm.from_name,
                        from_email: modalAddSenderForm.from_email,
                        reply_to_email: modalAddSenderForm.reply_to_email || null,
                        title: modalAddSenderForm.title || null,
                        is_default: modalAddSenderForm.is_default,
                      },
                      {
                        onSuccess: (res) => {
                          const newId = (res.data as { id?: string })?.id;
                          setModalShowAddSender(false);
                          toast.success("Sender profile added");
                          if (newId) setModalSenderIds((prev) => [...prev, newId]);
                        },
                        onError: (err: unknown) =>
                          setModalAddSenderError((err as Error)?.message ?? "Failed to create sender."),
                      },
                    );
                  }}
                >
                  <div className="form-group">
                    <label className="label">From Name</label>
                    <input
                      className="input-field"
                      value={modalAddSenderForm.from_name}
                      onChange={(e) => setModalAddSenderForm((f) => ({ ...f, from_name: e.target.value }))}
                      placeholder="e.g. Alex Morgan"
                    />
                  </div>
                  <div className="form-group">
                    <label className="label">Title</label>
                    <input
                      className="input-field"
                      value={modalAddSenderForm.title}
                      onChange={(e) => setModalAddSenderForm((f) => ({ ...f, title: e.target.value }))}
                      placeholder="e.g. Sales Development Rep"
                    />
                  </div>
                  <div className="form-group">
                    <label className="label">From Email</label>
                    <input
                      className="input-field"
                      type="email"
                      value={modalAddSenderForm.from_email}
                      onChange={(e) => setModalAddSenderForm((f) => ({ ...f, from_email: e.target.value }))}
                      placeholder="e.g. alex@yourdomain.com"
                    />
                  </div>
                  <div className="form-group">
                    <label className="label">Reply-To Email</label>
                    <input
                      className="input-field"
                      type="email"
                      value={modalAddSenderForm.reply_to_email}
                      onChange={(e) => setModalAddSenderForm((f) => ({ ...f, reply_to_email: e.target.value }))}
                      placeholder="Optional — defaults to From Email"
                    />
                  </div>
                  <div className="cbp-checkbox-row">
                    <input
                      type="checkbox"
                      id="modal-sender-default"
                      checked={modalAddSenderForm.is_default}
                      onChange={(e) => setModalAddSenderForm((f) => ({ ...f, is_default: e.target.checked }))}
                      className="cbp-checkbox-input"
                    />
                    <label htmlFor="modal-sender-default" className="cbp-checkbox-label">
                      Set as default sender
                    </label>
                  </div>
                  {modalAddSenderError && <p className="cbp-form-error">{modalAddSenderError}</p>}
                  <div className="cbp-form-actions">
                    <button type="submit" className="btn btn-primary" disabled={createSenderForModalMutation.isPending}>
                      Add Identity
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={() => setModalShowAddSender(false)}>
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Create Sequence Modal ──────────────────────────────────────────── */}
      {showSequenceModal && (
        <div className="wcm-overlay">
          <div className="wcm-modal wb-inline-max1200">
            {/* Header */}
            <div className="wcm-header">
              <h2 className="cbp-page-title wb-inline-title-lg">Sequence Builder</h2>
              <div className="wb-inline-status-row">
                {seqModalSaveStatus === "saving" && <span className="wb-inline-text-xs-muted">Saving…</span>}
                {seqModalSaveStatus === "saved" && (
                  <span className="wb-inline-text-xs-green">
                    <CheckCircle2 size={13} className="wb-inline-icon-inline" /> Saved
                  </span>
                )}
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    setShowSequenceModal(false);
                    resetSeqModal();
                  }}
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="sd-tabs">
              {(["Sequences", "Options"] as const).map((tab) => (
                <button
                  key={tab}
                  className={`sd-tab${seqModalActiveTab === tab ? " sd-tab--active" : ""}`}
                  onClick={() => setSeqModalActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Scrollable body */}
            <div className="wcm-body">
              {seqModalActiveTab === "Sequences" && (
                <>
                  {/* Sequence name */}
                  <div className="sd-name-field wb-inline-mb20">
                    <label className="sd-field-label">
                      Sequence Name <span className="sd-required">*</span>
                    </label>
                    <input
                      type="text"
                      className={`sd-subject-input${seqModalNameError ? " sd-input--error" : ""}`}
                      placeholder="Enter sequence name..."
                      value={seqModalName}
                      onChange={(e) => {
                        setSeqModalName(e.target.value);
                        if (e.target.value.trim()) setSeqModalNameError(false);
                      }}
                      onBlur={handleSeqModalNameBlur}
                      autoFocus
                    />
                    {seqModalNameError && <p className="sd-error-msg">Sequence name is required</p>}
                    {!seqModalId && seqModalName.trim() && (
                      <p className="wb-inline-hint-top">Tab or click away to save the name and begin building steps</p>
                    )}
                  </div>

                  {/* Editor layout — only shown after sequence is created */}
                  {seqModalId && (
                    <div className="sd-editor-layout">
                      {/* Step sidebar */}
                      <div className="sd-step-sidebar">
                        {(hasGoldPlusAccess ? seqModalSteps : seqModalSteps.slice(0, 1)).map((step, stepIndex) => (
                          <div key={step.id}>
                            {stepIndex > 0 && (
                              <div className="sd-delay-indicator">
                                <Clock size={14} />
                                <span>Send next message in</span>
                                <input
                                  type="number"
                                  min={1}
                                  className="sd-delay-input"
                                  value={seqModalDelayValues[step.id]?.value ?? 3}
                                  onChange={(e) => {
                                    const val = Math.max(1, parseInt(e.target.value, 10) || 1);
                                    setSeqModalDelayValues((prev) => ({
                                      ...prev,
                                      [step.id]: { value: val, unit: prev[step.id]?.unit ?? "Days" },
                                    }));
                                  }}
                                  onBlur={() => {
                                    if (!seqModalId) return;
                                    const { value: val, unit } = seqModalDelayValues[step.id] ?? {
                                      value: 3,
                                      unit: "Days",
                                    };
                                    updateSeqStepMutation.mutate(
                                      {
                                        sequenceId: seqModalId,
                                        stepId: step.id,
                                        data: { delay_config: { amount: val, unit: unit.toLowerCase() } },
                                      },
                                      { onError: () => toast.error("Failed to save delay") },
                                    );
                                  }}
                                />
                                <select
                                  className="sd-delay-unit"
                                  value={seqModalDelayValues[step.id]?.unit ?? "Days"}
                                  onChange={(e) => {
                                    if (!seqModalId) return;
                                    const unit = e.target.value;
                                    const val = seqModalDelayValues[step.id]?.value ?? 3;
                                    setSeqModalDelayValues((prev) => ({ ...prev, [step.id]: { value: val, unit } }));
                                    updateSeqStepMutation.mutate(
                                      {
                                        sequenceId: seqModalId,
                                        stepId: step.id,
                                        data: { delay_config: { amount: val, unit: unit.toLowerCase() } },
                                      },
                                      { onError: () => toast.error("Failed to save delay") },
                                    );
                                  }}
                                >
                                  <option>Days</option>
                                  <option>Hours</option>
                                </select>
                              </div>
                            )}
                            <div
                              className={`sd-step-card ${seqModalSelectedStepId === step.id ? "sd-step-card--active" : ""}`}
                              onClick={() => {
                                setSeqModalSelectedStepId(step.id);
                                const fv = step.variants[0];
                                if (fv) {
                                  setSeqModalSelectedVariantId(fv.id);
                                  setSeqModalLocalSubject(fv.subject_line ?? "");
                                  setSeqModalLocalBody(extractEmailBody(fv.email_body ?? ""));
                                }
                              }}
                            >
                              <div className="sd-step-card-header">
                                <span className="sd-step-label">{step.step_label}</span>
                                <div className="sd-step-card-actions">
                                  {stepIndex > 0 && (
                                    <button
                                      className="sd-step-delete-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSeqModalDeleteTarget({ stepId: step.id, stepLabel: step.step_label });
                                      }}
                                      title="Delete step"
                                    >
                                      <Trash2 size={13} />
                                    </button>
                                  )}
                                </div>
                              </div>
                              {step.variants.map((v, vIndex) => (
                                <div
                                  key={v.id}
                                  className={`sd-variant-item ${seqModalSelectedStepId === step.id && seqModalSelectedVariantId === v.id ? "sd-variant-item--active" : ""}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSeqModalSelectedStepId(step.id);
                                    setSeqModalSelectedVariantId(v.id);
                                    setSeqModalLocalSubject(v.subject_line ?? "");
                                    setSeqModalLocalBody(extractEmailBody(v.email_body ?? ""));
                                  }}
                                >
                                  <span className="sd-variant-label">Variant {v.variant_label}</span>
                                  <span className="sd-variant-subject">{v.subject_line ?? "(no subject)"}</span>
                                  {vIndex > 0 && (
                                    <button
                                      className="sd-delete-btn"
                                      onClick={(e) => handleSeqModalDeleteVariant(step.id, v.id, e)}
                                      title="Delete variant"
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  )}
                                </div>
                              ))}
                              {hasGoldPlusAccess && (
                                <button
                                  className="sd-add-variant-btn"
                                  onClick={(e) => handleSeqModalAddVariant(step.id, e)}
                                  disabled={addSeqVariantMutation.isPending}
                                >
                                  <Plus size={14} /> Add variant
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                        {hasGoldPlusAccess && seqModalAvailableTypes.length > 0 && (
                          <>
                            <div className="sd-step-type-select-wrap">
                              <select
                                value={seqModalNewStepType}
                                onChange={(e) => setSeqModalNewStepType(e.target.value as typeof seqModalNewStepType)}
                                className="sd-step-type-select"
                              >
                                {seqModalAvailableTypes.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <button
                              className="sd-add-step-btn"
                              onClick={handleSeqModalAddStep}
                              disabled={addSeqStepMutation.isPending}
                            >
                              <Plus size={16} /> Add Step
                            </button>
                          </>
                        )}
                      </div>

                      {/* Editor panel */}
                      <div className="sd-editor-panel">
                        <div className="sd-editor-header">
                          <h3 className="sd-editor-title">
                            {seqModalCurrentStep
                              ? `${seqModalCurrentStep.step_label} — Variant ${seqModalCurrentVariant?.variant_label ?? ""}`
                              : "Select a step"}
                          </h3>
                          <span className="sd-autosave">
                            {seqModalSaveStatus === "saved" && <CheckCircle2 size={14} />}
                            {seqModalSaveStatus === "saving"
                              ? "Saving…"
                              : seqModalSaveStatus === "saved"
                                ? "Saved ✓"
                                : seqModalSaveStatus === "error"
                                  ? "Save failed"
                                  : ""}
                          </span>
                        </div>
                        <div className="sd-editor-body">
                          <label className="sd-field-label">
                            Subject Line <span className="sd-required">*</span>
                          </label>
                          <input
                            type="text"
                            className={`sd-subject-input${seqModalSubjectError ? " sd-input--error" : ""}`}
                            placeholder="Enter subject line..."
                            value={seqModalLocalSubject}
                            onChange={(e) => {
                              setSeqModalLocalSubject(e.target.value);
                              if (e.target.value.trim()) setSeqModalSubjectError(false);
                            }}
                            disabled={!seqModalCurrentVariant}
                          />
                          {seqModalSubjectError && <p className="sd-error-msg">Subject line is required</p>}
                          <div className="sd-body-field">
                            <div className="sd-body-field-header">
                              <label className="sd-field-label" style={{ margin: 0 }}>
                                Email Body (HTML) <span className="sd-required">*</span>
                              </label>
                              <div className="sd-view-toggle">
                                <button
                                  type="button"
                                  className={`sd-view-btn${seqModalEmailViewMode === "editor" ? " sd-view-btn--active" : ""}`}
                                  onClick={() => {
                                    setSeqModalEmailViewMode("editor");
                                    setSeqModalPreviewHtml(null);
                                  }}
                                >
                                  &lt;/&gt; Editor
                                </button>
                                <button
                                  type="button"
                                  className={`sd-view-btn${seqModalEmailViewMode === "preview" ? " sd-view-btn--active" : ""}`}
                                  onClick={async () => {
                                    setSeqModalEmailViewMode("preview");
                                    if (!seqModalLocalBody) {
                                      setSeqModalPreviewHtml(null);
                                      return;
                                    }

                                    // If the body is a full HTML document, render it directly
                                    // in the iframe without wrapping in the LeadNova template.
                                    const isFullDoc =
                                      /<!DOCTYPE\s+html/i.test(seqModalLocalBody) ||
                                      /<html[\s>]/i.test(seqModalLocalBody);
                                    if (isFullDoc) {
                                      setSeqModalPreviewHtml(seqModalLocalBody);
                                      return;
                                    }

                                    setSeqModalPreviewLoading(true);
                                    try {
                                      const raw = localStorage.getItem(STORAGE);
                                      const stored = raw ? JSON.parse(raw) : null;
                                      const token = stored?.token ?? stored?.access_token ?? "";
                                      const res = await fetch(API_URL.SEQUENCES.EMAIL_PREVIEW, {
                                        method: "POST",
                                        headers: {
                                          Authorization: `Bearer ${token}`,
                                          "Content-Type": "application/json",
                                        },
                                        body: JSON.stringify({
                                          body: seqModalLocalBody,
                                          from_name: "Your Team",
                                          footer_text: seqModalFooterText,
                                          unsubscribe_link: seqModalUnsubscribeLink ? "1" : "0",
                                        }),
                                      });
                                      if (res.ok) {
                                        setSeqModalPreviewHtml(await res.text());
                                      } else {
                                        setSeqModalPreviewHtml(null);
                                        toast.error("Failed to load email preview.");
                                      }
                                    } catch {
                                      setSeqModalPreviewHtml(null);
                                      toast.error("Failed to load email preview.");
                                    } finally {
                                      setSeqModalPreviewLoading(false);
                                    }
                                  }}
                                >
                                  &#128065; Preview
                                </button>
                              </div>
                            </div>
                            {seqModalEmailViewMode === "editor" ? (
                              <EmailEditor
                                value={seqModalLocalBody}
                                onChange={(v) => {
                                  setSeqModalLocalBody(v);
                                  if (bodyHasContent(v)) setSeqModalBodyError(false);
                                }}
                                disabled={!seqModalCurrentVariant}
                                saveStatus={seqModalSaveStatus}
                                hasError={seqModalBodyError}
                                onSave={handleSeqModalSaveVariant}
                              />
                            ) : seqModalPreviewLoading ? (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  height: "100%",
                                  color: "#94a3b8",
                                }}
                              >
                                Loading preview…
                              </div>
                            ) : (
                              <iframe
                                srcDoc={
                                  seqModalPreviewHtml ??
                                  "<p style='color:#a0aec0;font-family:Arial,sans-serif;padding:20px;'>No content to preview.</p>"
                                }
                                className="sd-email-preview-frame"
                                title="Email Preview"
                                sandbox="allow-same-origin"
                              />
                            )}
                            {seqModalBodyError && <p className="sd-error-msg">Email body is required</p>}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {!seqModalId && (
                    <div className="wb-inline-empty-state">
                      <GitBranch size={40} className="wb-inline-empty-icon" />
                      <p className="wb-inline-empty-text">
                        Enter a sequence name above and tab away to start building steps
                      </p>
                    </div>
                  )}
                </>
              )}

              {seqModalActiveTab === "Options" && (
                <div className="sd-options">
                  <div className="card sd-options-card">
                    <h3 className="sd-section-title">
                      <MessageSquare size={18} /> Reply Handling
                    </h3>
                    <div className="sd-toggle-row">
                      <span>Sequence on Reply</span>
                      <button
                        className={`sd-toggle ${seqModalSequenceOnReply ? "sd-toggle--on" : ""}`}
                        onClick={() => setSeqModalSequenceOnReply(!seqModalSequenceOnReply)}
                      >
                        {seqModalSequenceOnReply ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                      </button>
                    </div>
                  </div>

                  <div className="card sd-options-card">
                    <h3 className="sd-section-title">
                      <Shield size={18} /> Bounce Handling
                    </h3>
                    <div className="sd-toggle-row">
                      <span>Block on Bounce</span>
                      <button
                        className={`sd-toggle ${seqModalBlockOnBounce ? "sd-toggle--on" : ""}`}
                        onClick={() => setSeqModalBlockOnBounce(!seqModalBlockOnBounce)}
                      >
                        {seqModalBlockOnBounce ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                      </button>
                    </div>
                  </div>

                  <div className="card sd-options-card">
                    <h3 className="sd-section-title">
                      <Link2 size={18} /> Unsubscribe
                    </h3>
                    <div className="sd-toggle-row">
                      <span>Include unsubscribe link</span>
                      <button
                        className={`sd-toggle ${seqModalUnsubscribeLink ? "sd-toggle--on" : ""}`}
                        onClick={() => setSeqModalUnsubscribeLink(!seqModalUnsubscribeLink)}
                      >
                        {seqModalUnsubscribeLink ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                      </button>
                    </div>
                  </div>

                  <div className="card sd-options-card">
                    <h3 className="sd-section-title">Footer Text</h3>
                    <textarea
                      className="sd-footer-textarea"
                      value={seqModalFooterText}
                      onChange={(e) => setSeqModalFooterText(e.target.value)}
                    />
                  </div>

                  <button
                    className="btn btn-primary"
                    onClick={handleSeqModalSaveOptions}
                    disabled={!seqModalId || updateSeqNameMutation.isPending}
                  >
                    Save Options
                  </button>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="wb-inline-modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowSequenceModal(false);
                  resetSeqModal();
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSeqModalActivate}
                disabled={!seqModalId || activateSeqModalMutation.isPending}
              >
                <Send size={15} /> {activateSeqModalMutation.isPending ? "Activating…" : "Activate & Select"}
              </button>
            </div>
          </div>

          {/* Delete step confirmation */}
          {seqModalDeleteTarget && (
            <div className="cbp-sender-overlay" onClick={() => setSeqModalDeleteTarget(null)}>
              <div className="cbp-sender-modal wb-inline-max380" onClick={(e) => e.stopPropagation()}>
                <h3 className="cbp-modal-title wb-inline-mb8">Delete Step</h3>
                <p className="wb-inline-confirm-desc">
                  Delete &ldquo;{seqModalDeleteTarget.stepLabel}&rdquo;? This will also remove all its variants.
                </p>
                <div className="wb-inline-confirm-actions">
                  <button className="btn btn-secondary" onClick={() => setSeqModalDeleteTarget(null)}>
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary wb-inline-btn-danger"
                    onClick={handleSeqModalDeleteStep}
                    disabled={deleteSeqStepMutation.isPending}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
